import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  FiBookOpen,
  FiCalendar,
  FiMail,
  FiCheck,
  FiClock,
  FiArrowRight,
  FiTrendingUp,
  FiBook,
  FiRepeat,
  FiStar,
  FiTarget,
  FiAward,
  FiGift,
  FiPlay,
  FiRefreshCw,
  FiZap,
  FiHeart,
  FiCheckCircle,
  FiGlobe,
  FiLayers,
  FiCpu,
  FiBarChart,
  FiMinus,
  FiLifeBuoy,
  FiLock,
  FiUser,
  FiCoffee,
  FiInfo,
  FiActivity,
  FiTrash,
  FiEye,
  FiBookmark,
  FiDownload,
} from 'react-icons/fi';
import InfoTooltip from '../../../components/InfoTooltip';
import GetStartedWith from '../../../components/GetStartedWith';
import GREWordsChallenge from '../../../components/GREWordsChallenge';
import '../../../components/GREWordsChallenge.css';
import Sidebar from '../../Sidebar';
import { useNavigate, useLocation } from 'react-router-dom';
import greWordsData from '../../../utils/greVocabularyData';
import {
  getCachedComprehensiveProgress,
  getVocabularyProgressFromComprehensive,
  clearProgressCache,
  clearVerbalProgressCache,
  getComprehensiveVerbalProgress,
} from './api/progressAPI';
import { getStoredUser } from '../../../utils/authSession';
import {
  getAuthHeaders,
  API_BASE_URL,
  getAccountScopedLocalStorageKey,
  clearAllCaches,
  isTokenValid,
  checkTokenExpiration,
  toastdisplay,
  setLocalStorageWithSync,
  speakWord,
} from '../../../utils/utils';
import { fetchBoostStatus as fetchSharedBoostStatus } from './api/boostStatusAPI';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import { getGreArticlePublicRef } from '../../../utils/greArticlePublicCode';
import {
  getGreArticleCacheKey,
  getGreArticleExercisesCacheKey,
} from './articleCacheKeys';
import {
  readGreCompletionDateFromLocalCache,
  readGreDetailedResultFromLocalCache,
} from './api/greResultCache';
import '../GRE.css';

// Training Plan API function
const getActiveTrainingPlan = async (userEmail, setToast) => {
  if (!isTokenValid()) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/gre/training-plans/active`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const expired = await checkTokenExpiration(response, () => {}, setToast);
    if (expired) return;

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching active training plan:', error);
    throw error;
  }
};

const cacheGreArticleBundle = async (article) => {
  const articleRef = getGreArticlePublicRef(article) || article?.id;
  if (!articleRef) {
    return;
  }

  const cacheUser = getStoredUser();

  await setLocalStorageWithSync(
    getGreArticleCacheKey(cacheUser, articleRef),
    JSON.stringify({
      id: article.id,
      public_code: article.public_code,
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      difficulty_level: article.difficulty_level,
      estimated_reading_time: article.estimated_reading_time,
      created_at: article.created_at,
      words: article.words,
    })
  );

  if (
    (article.exercises && article.exercises.length > 0) ||
    (article.rc_exercises && article.rc_exercises.length > 0)
  ) {
    await setLocalStorageWithSync(
      getGreArticleExercisesCacheKey(cacheUser, articleRef),
      JSON.stringify({
        word_exercises: article.exercises || [],
        rc_exercises: article.rc_exercises || [],
      })
    );
  }
};

const enableArticleBoost = async (
  userEmail,
  boostMultiplier = 2,
  durationDays = 7
) => {
  if (!isTokenValid()) {
    throw new Error('Authentication required');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/gre/boost-articles`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        boost_multiplier: boostMultiplier,
        duration_days: durationDays,
      }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearAllCaches();
        throw new Error('Authentication failed');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error enabling article boost:', error);
    throw error;
  }
};

const deactivateArticleBoost = async (userEmail) => {
  if (!isTokenValid()) {
    throw new Error('Authentication required');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/gre/boost-articles`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearAllCaches();
        throw new Error('Authentication failed');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deactivating article boost:', error);
    throw error;
  }
};

// SessionStorage keys for caching
const SESSION_KEYS = {
  ENROLLMENT: 'gre_vocab_enrollment_session',
  DAILY_ARTICLES: 'gre_vocab_daily_articles_session',
  CACHE_TIMESTAMP: 'gre_vocab_cache_timestamp',
  READ_ARTICLES: 'gre_vocab_read_articles',
  ARTICLE_PROGRESS: 'gre_vocab_article_progress',
  OPENED_ARTICLES: 'gre_vocab_opened_articles',
};

// Cache duration (24 hours for daily articles, longer for enrollment)
const CACHE_DURATION = {
  ENROLLMENT: 7 * 24 * 60 * 60 * 1000, // 7 days
  DAILY_ARTICLES: 24 * 60 * 60 * 1000, // 24 hours
};

// Helper functions for sessionStorage management
const clearVocabularyCache = () => {
  Object.values(SESSION_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
    sessionStorage.removeItem(`${key}_timestamp`);
  });
};

// Export function to clear vocabulary cache (can be called from other components)
export const clearVocabularyCacheExternal = () => {
  clearVocabularyCache();
};

// Helper functions for managing read articles
const markArticleAsRead = async (articleId, userEmail) => {
  try {
    if (!userEmail) return;
    const readArticles = getCachedData(SESSION_KEYS.READ_ARTICLES) || [];
    if (!readArticles.includes(articleId)) {
      readArticles.push(articleId);
      setCachedData(SESSION_KEYS.READ_ARTICLES, readArticles);
    }

    // Also store in localStorage for persistence
    const localStorageKey = `gre_vocab_read_articles_${userEmail}`;
    const localStorageReadArticles = JSON.parse(
      localStorage.getItem(localStorageKey) || '[]'
    );
    if (!localStorageReadArticles.includes(articleId)) {
      localStorageReadArticles.push(articleId);
      await setLocalStorageWithSync(
        localStorageKey,
        JSON.stringify(localStorageReadArticles)
      );
    }
  } catch (error) {
    console.error('Error marking article as read:', error);
  }
};

const isArticleRead = (articleId, userEmail) => {
  try {
    if (!userEmail) return false;
    // Check sessionStorage first
    const readArticles = getCachedData(SESSION_KEYS.READ_ARTICLES) || [];
    if (readArticles.includes(articleId)) {
      return true;
    }

    // Check localStorage for persistence
    const localStorageKey = `gre_vocab_read_articles_${userEmail}`;
    const localStorageReadArticles = JSON.parse(
      localStorage.getItem(localStorageKey) || '[]'
    );
    return localStorageReadArticles.includes(articleId);
  } catch (error) {
    console.error('Error checking if article is read:', error);
    return false;
  }
};

// Export function to mark article as read (can be called from other components)
export const markArticleAsReadExternal = (articleId) => {
  // Get userEmail from sessionStorage; do not persist user progress globally.
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  if (user?.email) {
    markArticleAsRead(articleId, user.email);
  }
};

// Enhanced article progress tracking functions
const markArticleAsOpened = async (articleId, userEmail) => {
  try {
    if (!userEmail) return;
    const openedArticles = getCachedData(SESSION_KEYS.OPENED_ARTICLES) || [];
    if (!openedArticles.includes(articleId)) {
      openedArticles.push(articleId);
      setCachedData(SESSION_KEYS.OPENED_ARTICLES, openedArticles);
    }

    // Also store in localStorage for persistence
    const localStorageKey = `gre_vocab_opened_articles_${userEmail}`;
    const localStorageOpenedArticles = JSON.parse(
      localStorage.getItem(localStorageKey) || '[]'
    );
    if (!localStorageOpenedArticles.includes(articleId)) {
      localStorageOpenedArticles.push(articleId);
      await setLocalStorageWithSync(
        localStorageKey,
        JSON.stringify(localStorageOpenedArticles)
      );
    }
  } catch (error) {
    console.error('Error marking article as opened:', error);
  }
};

const updateArticleProgress = async (articleId, progressData, userEmail) => {
  try {
    if (!userEmail) return;
    const allProgress = getCachedData(SESSION_KEYS.ARTICLE_PROGRESS) || {};
    allProgress[articleId] = {
      ...allProgress[articleId],
      ...progressData,
      lastUpdated: Date.now(),
    };
    setCachedData(SESSION_KEYS.ARTICLE_PROGRESS, allProgress);

    // Also store in localStorage for persistence
    const localStorageKey = `gre_vocab_article_progress_${userEmail}`;
    const localStorageProgress = JSON.parse(
      localStorage.getItem(localStorageKey) || '{}'
    );
    localStorageProgress[articleId] = {
      ...localStorageProgress[articleId],
      ...progressData,
      lastUpdated: Date.now(),
    };
    await setLocalStorageWithSync(
      localStorageKey,
      JSON.stringify(localStorageProgress)
    );
  } catch (error) {
    console.error('Error updating article progress:', error);
  }
};

const getArticleProgress = (articleId, userEmail) => {
  try {
    if (!userEmail) {
      return {
        exercisesCompleted: 0,
        totalExercises: 0,
        lastUpdated: null,
      };
    }
    // Check sessionStorage first
    const allProgress = getCachedData(SESSION_KEYS.ARTICLE_PROGRESS) || {};
    if (allProgress[articleId]) {
      return allProgress[articleId];
    }

    // Check localStorage for persistence
    const localStorageKey = `gre_vocab_article_progress_${userEmail}`;
    const localStorageProgress = JSON.parse(
      localStorage.getItem(localStorageKey) || '{}'
    );
    if (localStorageProgress[articleId]) {
      return localStorageProgress[articleId];
    }

    // Return default if not found
    return {
      exercisesCompleted: 0,
      totalExercises: 0,
      wordsMastered: 0,
      totalWords: 0,
      completedExerciseIds: [],
      masteredWordExerciseIds: [],
      status: 'not_started',
      lastUpdated: null,
    };
  } catch (error) {
    console.error('Error getting article progress:', error);
    return {
      exercisesCompleted: 0,
      totalExercises: 0,
      wordsMastered: 0,
      totalWords: 0,
      completedExerciseIds: [],
      masteredWordExerciseIds: [],
      status: 'not_started',
      lastUpdated: null,
    };
  }
};

const getArticleExerciseTotal = (article) =>
  (article?.exercises?.length || 0) + (article?.rc_exercises?.length || 0);

const getNormalizedArticleProgress = (articleId, userEmail, article) => {
  const progress = getArticleProgress(articleId, userEmail);
  const totalExercises = Math.max(
    progress.totalExercises || 0,
    getArticleExerciseTotal(article)
  );
  const totalWords = Math.max(
    progress.totalWords || 0,
    article?.words?.length || 0
  );

  const completedExerciseIds = Array.isArray(progress.completedExerciseIds)
    ? Array.from(new Set(progress.completedExerciseIds.map(String)))
    : [];
  const masteredWordExerciseIds = Array.isArray(progress.masteredWordExerciseIds)
    ? Array.from(new Set(progress.masteredWordExerciseIds.map(String)))
    : [];

  const exercisesCompleted = completedExerciseIds.length
    ? completedExerciseIds.length
    : progress.exercisesCompleted || 0;
  const wordsMastered = masteredWordExerciseIds.length
    ? masteredWordExerciseIds.length
    : progress.wordsMastered || 0;

  return {
    ...progress,
    totalExercises,
    totalWords,
    completedExerciseIds,
    masteredWordExerciseIds,
    exercisesCompleted:
      totalExercises > 0
        ? Math.min(exercisesCompleted, totalExercises)
        : exercisesCompleted,
    wordsMastered:
      totalWords > 0 ? Math.min(wordsMastered, totalWords) : wordsMastered,
  };
};

const getArticleStatus = (
  articleId,
  userEmail,
  totalExercises,
  totalWords = 0
) => {
  if (!userEmail) return 'locked';

  const progress = getNormalizedArticleProgress(articleId, userEmail, {
    exercises: Array.from({ length: totalExercises }),
    words: Array.from({ length: totalWords }),
  });

  // Check if article is opened (sessionStorage first, then localStorage)
  let isOpened =
    getCachedData(SESSION_KEYS.OPENED_ARTICLES)?.includes(articleId) || false;
  if (!isOpened) {
    const localStorageKey = `gre_vocab_opened_articles_${userEmail}`;
    const localStorageOpenedArticles = JSON.parse(
      localStorage.getItem(localStorageKey) || '[]'
    );
    isOpened = localStorageOpenedArticles.includes(articleId);
  }

  // Check if article is read (sessionStorage first, then localStorage)
  let isRead =
    getCachedData(SESSION_KEYS.READ_ARTICLES)?.includes(articleId) || false;
  if (!isRead) {
    const localStorageKey = `gre_vocab_read_articles_${userEmail}`;
    const localStorageReadArticles = JSON.parse(
      localStorage.getItem(localStorageKey) || '[]'
    );
    isRead = localStorageReadArticles.includes(articleId);
  }

  if (totalExercises > 0 && progress.exercisesCompleted >= totalExercises) {
    if (totalWords > 0 && progress.wordsMastered >= totalWords) {
      return 'done';
    }
    return 'completed';
  } else if (progress.exercisesCompleted > 0 || isRead) {
    return 'read';
  } else if (isOpened) {
    return 'opened';
  } else {
    return 'not_started';
  }
};

// Export functions for external use
export const markArticleAsOpenedExternal = (articleId) => {
  // Get userEmail from sessionStorage or use default
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const userEmail = user?.email || 'default';
  markArticleAsOpened(articleId, userEmail);
};

export const updateArticleProgressExternal = (articleId, progressData) => {
  // Get userEmail from sessionStorage or use default
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const userEmail = user?.email || 'default';
  updateArticleProgress(articleId, progressData, userEmail);
};

export const getArticleProgressExternal = (articleId) => {
  // Get userEmail from sessionStorage or use default
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const userEmail = user?.email || 'default';
  return getArticleProgress(articleId, userEmail);
};

export const getArticleStatusExternal = (
  articleId,
  totalExercises,
  totalWords = 0
) => {
  // Get userEmail from sessionStorage; never use a shared default bucket.
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  return getArticleStatus(articleId, user?.email, totalExercises, totalWords);
};

// Initialize sessionStorage from localStorage for immediate access
const initializeSessionFromLocalStorage = (userEmail) => {
  if (!userEmail) return;

  try {
    const emailKey = userEmail;

    // Initialize read articles
    const readArticlesKey = `gre_vocab_read_articles_${emailKey}`;
    const localStorageReadArticles = JSON.parse(
      localStorage.getItem(readArticlesKey) || '[]'
    );
    if (localStorageReadArticles.length > 0) {
      setCachedData(SESSION_KEYS.READ_ARTICLES, localStorageReadArticles);
    }

    // Initialize opened articles
    const openedArticlesKey = `gre_vocab_opened_articles_${emailKey}`;
    const localStorageOpenedArticles = JSON.parse(
      localStorage.getItem(openedArticlesKey) || '[]'
    );
    if (localStorageOpenedArticles.length > 0) {
      setCachedData(SESSION_KEYS.OPENED_ARTICLES, localStorageOpenedArticles);
    }

    // Initialize article progress
    const progressKey = `gre_vocab_article_progress_${emailKey}`;
    const localStorageProgress = JSON.parse(
      localStorage.getItem(progressKey) || '{}'
    );
    if (Object.keys(localStorageProgress).length > 0) {
      setCachedData(SESSION_KEYS.ARTICLE_PROGRESS, localStorageProgress);
    }
  } catch (error) {
    console.error('Error initializing session from localStorage:', error);
  }
};

// Helper function to check if user has taken the first mock test
const hasUserTakenFirstMockTest = (userEmail) => {
  if (!userEmail) return false;

  // Check localStorage for test completion
  const test1Completion = readGreCompletionDateFromLocalCache(
    userEmail,
    'full-test-1'
  );
  if (test1Completion) return true;

  // Check for detailed results
  const test1Results = readGreDetailedResultFromLocalCache(
    userEmail,
    'full-test-1'
  );
  if (test1Results) return true;

  // Check for training plan existence (indicates test was taken)
  const trainingPlan = localStorage.getItem('liwaza-training-plan');
  if (trainingPlan) {
    try {
      const parsed = JSON.parse(trainingPlan);
      return parsed && parsed.duration_weeks;
    } catch (error) {
      return false;
    }
  }

  return false;
};

// Vocabulary Game Section Component
const VocabularyGameSection = ({ progress, userEmail, navigate }) => {
  const [showGame, setShowGame] = useState(false);

  // Calculate numberOfWords based on mastered + review words
  const calculateNumberOfWords = () => {
    const masteredWords = progress?.masteredWordsList || [];
    const wordsNeedingPractice = progress?.wordsNeedingPractice || [];
    const totalWords = masteredWords.length + wordsNeedingPractice.length;

    // Allow unlimited words if user has mastered many, minimum 10
    return Math.max(totalWords, 10);
  };

  const [gameSettings, setGameSettings] = useState({
    numberOfWords: calculateNumberOfWords(),
    timeBetweenQuestions: 10,
    gameMode: 'riddle',
  });

  // Update numberOfWords when progress changes
  useEffect(() => {
    const newNumberOfWords = calculateNumberOfWords();
    setGameSettings((prev) => ({
      ...prev,
      numberOfWords: newNumberOfWords,
    }));
  }, [
    progress?.masteredWordsList?.length,
    progress?.wordsNeedingPractice?.length,
  ]);

  // Get played words from localStorage
  const getPlayedWords = () => {
    try {
      const playedWordsKey = getAccountScopedLocalStorageKey(
        'gre_words_played',
        userEmail
      );
      if (!playedWordsKey) return [];
      const playedWords = JSON.parse(
        localStorage.getItem(playedWordsKey) || '[]'
      );
      return playedWords;
    } catch (error) {
      console.error('Error loading played words:', error);
      return [];
    }
  };

  // Load GRE words data
  const loadGreWordsData = () => {
    try {
      // Return the imported GRE words data
      return greWordsData || [];
    } catch (error) {
      console.error('Error loading GRE words:', error);
      return [];
    }
  };

  // Proper Fisher-Yates shuffle algorithm
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array]; // Create a copy to avoid mutating original
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // Get available words for the game (mastered + practice + new words)
  const getAvailableWordsForGame = useCallback(() => {
    const greWordsDataList = loadGreWordsData();
    if (!greWordsDataList || greWordsDataList.length === 0) {
      return [];
    }

    const masteredWords = progress?.masteredWordsList || [];
    const wordsNeedingPractice = progress?.wordsNeedingPractice || [];
    const playedWords = getPlayedWords();

    // Combine all available words (mastered + practice + new)
    let availableWords = [];

    // Sort mastered words by date (most recent first) - prioritize latest mastered words
    const sortedMasteredWords = [...masteredWords].sort((a, b) => {
      const dateA = new Date(a.masteredAt || a.lastPracticedAt || 0);
      const dateB = new Date(b.masteredAt || b.lastPracticedAt || 0);
      return dateB - dateA; // Most recent first
    });

    // Sort practice words by date (most recent first)
    const sortedPracticeWords = [...wordsNeedingPractice].sort((a, b) => {
      const dateA = new Date(a.lastPracticedAt || a.firstSeenAt || 0);
      const dateB = new Date(b.lastPracticedAt || b.firstSeenAt || 0);
      return dateB - dateA; // Most recent first
    });

    // Add mastered words (not already played) - already sorted by most recent
    const masteredWordStrings = sortedMasteredWords
      .filter((wordObj) => !playedWords.includes(wordObj.word))
      .map((wordObj) => wordObj.word);

    // Add words needing practice (not already played) - already sorted by most recent
    const practiceWordStrings = sortedPracticeWords
      .filter((wordObj) => !playedWords.includes(wordObj.word))
      .map((wordObj) => wordObj.word);

    // Add new words (not mastered, not in practice, not played today)
    const allMasteredStrings = masteredWords.map((wordObj) => wordObj.word);
    const allPracticeStrings = wordsNeedingPractice.map(
      (wordObj) => wordObj.word
    );

    const newWords = greWordsDataList.filter(
      (wordData) =>
        !playedWords.includes(wordData.word) &&
        !allMasteredStrings.includes(wordData.word) &&
        !allPracticeStrings.includes(wordData.word)
    );

    // Shuffle new words only
    const shuffledNewWords = shuffleArray(newWords.map((w) => w.word));

    // Combine with priority: latest mastered words first, then practice words, then new words
    // Keep the order for mastered and practice words (latest first), shuffle only new words
    const allAvailableWordStrings = [
      ...masteredWordStrings,
      ...practiceWordStrings,
      ...shuffledNewWords,
    ];

    // Get full word data for all available words
    availableWords = allAvailableWordStrings
      .map((word) =>
        greWordsDataList.find((wordData) => wordData.word === word)
      )
      .filter(Boolean);

    return availableWords;
  }, [
    progress?.masteredWordsList,
    progress?.wordsNeedingPractice,
    shuffleArray,
  ]);

  const handleStartGame = () => {
    const availableWords = getAvailableWordsForGame();

    if (availableWords.length === 0) {
      toastdisplay('No words available for the game', false, 3000);
      return;
    }

    setShowGame(true);
  };

  const handleCloseGame = () => {
    setShowGame(false);
  };

  const masteredWordsCount = progress?.masteredWordsList?.length || 0;
  const wordsNeedingPracticeCount = progress?.wordsNeedingPractice?.length || 0;

  // Memoize available words to prevent recalculation on every render
  const availableWords = useMemo(() => {
    return getAvailableWordsForGame();
  }, [getAvailableWordsForGame]);

  const totalAvailableWords = availableWords.length;

  return (
    <>
      <div className="vocabulary_game_section">
        <h3 className="vocabulary_game_header">
          <FiTarget /> Word Review
        </h3>

        <div className="vocabulary_game_card">
          <div className="vocabulary_game_content">
            <div className="vocabulary_game_icon">
              <FiRefreshCw size={32} />
            </div>

            <div className="vocabulary_game_info">
              <h4 className="vocabulary_game_title">GRE Word Challenge</h4>
              <p className="vocabulary_game_description">
                Review the words you've already studied with an interactive
                game. Test your memory and reinforce your learning!
              </p>

              <div className="vocabulary_game_stats">
                <div className="vocabulary_game_stat">
                  <FiStar size={16} />
                  <span>{masteredWordsCount} words mastered</span>
                </div>
                <div className="vocabulary_game_stat">
                  <FiRepeat size={16} />
                  <span>{wordsNeedingPracticeCount} words to review</span>
                </div>
                <div className="vocabulary_game_stat">
                  <FiTarget size={16} />
                  <span>{totalAvailableWords} words available</span>
                </div>
              </div>

              <div className="vocabulary_game_inline_settings">
                <label className="vocabulary_game_setting">
                  <span>Game mode</span>
                  <select
                    value={gameSettings.gameMode}
                    onChange={(e) =>
                      setGameSettings((s) => ({
                        ...s,
                        gameMode: e.target.value,
                      }))
                    }
                    className="vocabulary_game_setting_select"
                  >
                    <option value="riddle">Riddle (definition quiz)</option>
                    <option value="word-to-definition">Word → Definition</option>
                    <option value="definition-to-word">Definition → Word</option>
                    <option value="mixed">Mixed (rotates each question)</option>
                  </select>
                </label>
                <label className="vocabulary_game_setting">
                  <span>Pause (sec)</span>
                  <input
                    type="number"
                    min={3}
                    max={45}
                    value={gameSettings.timeBetweenQuestions}
                    onChange={(e) =>
                      setGameSettings((s) => ({
                        ...s,
                        timeBetweenQuestions: Math.min(
                          45,
                          Math.max(3, parseInt(e.target.value, 10) || 10)
                        ),
                      }))
                    }
                    className="vocabulary_game_setting_input"
                  />
                </label>
              </div>
            </div>
          </div>

          <button
            className="vocabulary_game_configure_btn"
            onClick={handleStartGame}
            disabled={totalAvailableWords === 0}
          >
            <FiPlay size={16} />
            {totalAvailableWords === 0 ? 'No words available' : 'Start Game'}
          </button>
        </div>
      </div>

      {/* Game Component */}
      {showGame && (
        <GREWordsChallenge
          availableWords={availableWords}
          onClose={handleCloseGame}
          showPremiumLimits={false}
          initialGameMode={gameSettings.gameMode}
          initialWordCount={Math.min(
            gameSettings.numberOfWords,
            totalAvailableWords
          )}
          initialTimeBetweenQuestions={gameSettings.timeBetweenQuestions}
          autoStart={false}
        />
      )}
    </>
  );
};

// Helper function to get training plan duration in months
const getTrainingPlanDurationMonths = async (userEmail = null, setToast) => {
  // First check localStorage
  const trainingPlan = localStorage.getItem('liwaza-training-plan');
  if (trainingPlan) {
    try {
      const parsed = JSON.parse(trainingPlan);
      if (parsed && parsed.duration_weeks) {
        // Convert weeks to months (approximately)
        const months = Math.ceil(parsed.duration_weeks / 4);
        return Math.max(months, 1);
      }
    } catch (error) {
      console.error('Error parsing training plan from localStorage:', error);
    }
  }

  // If not found in localStorage and userEmail is provided, check backend
  if (userEmail) {
    try {
      const response = await getActiveTrainingPlan(userEmail, setToast);
      if (response.success && response.plan && response.plan.duration_weeks) {
        // Store complete plan data in localStorage for future use
        // Extract individual scores from totalScore (assuming 130+130=260 from the example)
        const totalScore = response.plan.scoreAnalysis?.totalScore || 260;
        const verbalScore = Math.floor(totalScore / 2); // Half of total score
        const quantScore = totalScore - verbalScore; // Remaining half
        const awaScore = 2; // Default AWA score from API response

        const planData = {
          title: response.plan.title,
          description: response.plan.description,
          duration_weeks: response.plan.duration_weeks,
          target_scores: response.plan.target_scores,
          improvement_needed: response.plan.improvement_needed,
          weekly_goals: response.plan.weekly_goals,
          recommended_resources: response.plan.recommended_resources,
          practice_schedule: response.plan.practice_schedule,
          scoreAnalysis: {
            ...response.plan.scoreAnalysis,
            // Add individual scores for Home.js compatibility
            verbalScore: verbalScore,
            quantScore: quantScore,
            awaScore: awaScore,
          },
          dailyTasks: response.plan.dailyTasks,
          milestones: response.plan.milestones,
          englishPlan: response.plan.englishPlan,
          completion_percentages: response.plan.completion_percentages,
          generatedAt: response.plan.generatedAt,
        };
        await setLocalStorageWithSync(
          'liwaza-training-plan',
          JSON.stringify(planData)
        );

        // Convert weeks to months (approximately)
        const months = Math.ceil(response.plan.duration_weeks / 4);
        return Math.max(months, 1);
      }
    } catch (error) {
      console.error('Error fetching training plan from backend:', error);
    }
  }

  return null;
};

// Helper function to calculate articles per day based on duration
const calculateArticlesPerDay = (durationMonths) => {
  const daysInMonths = durationMonths * 30;
  let articlesPerDay;

  // Fixed schedule based on duration
  if (daysInMonths <= 30) {
    articlesPerDay = 6;
  } else if (daysInMonths <= 60) {
    articlesPerDay = 3;
  } else if (daysInMonths <= 90) {
    articlesPerDay = 2;
  } else if (daysInMonths <= 120) {
    articlesPerDay = 2;
  } else {
    articlesPerDay = 2; // 150+ days
  }

  return {
    articlesPerDay,
    totalArticles: articlesPerDay * daysInMonths,
    wordsPerDay: articlesPerDay * 5, // Assuming 5 words per article
  };
};

const isCacheValid = (cacheKey, maxAge) => {
  try {
    const timestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);
    if (!timestamp) return false;

    const age = Date.now() - parseInt(timestamp);
    return age < maxAge;
  } catch (error) {
    return false;
  }
};

const setCachedData = (key, data) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
    sessionStorage.setItem(`${key}_timestamp`, Date.now().toString());
  } catch (error) {
    console.error('Error caching data:', error);
  }
};

const getCachedData = (key) => {
  try {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error reading cached data:', error);
    return null;
  }
};

// Enhanced API functions with sessionStorage caching
const checkEnrollmentCached = async (userEmail) => {
  // Early return if userEmail is not provided
  if (!userEmail) {
    console.warn('checkEnrollmentCached: userEmail is null or empty');
    throw new Error('User email is required for enrollment check');
  }

  if (!isTokenValid()) {
    return null;
  }

  // Check sessionStorage first
  if (isCacheValid(SESSION_KEYS.ENROLLMENT, CACHE_DURATION.ENROLLMENT)) {
    const cachedData = getCachedData(SESSION_KEYS.ENROLLMENT);
    if (cachedData) {
      return cachedData;
    }
  }

  // Make API call if no valid cache
  try {
    const response = await fetch(`${API_BASE_URL}/api/gre/enrollment`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token invalid, clear all caches
        clearAllCaches();
        throw new Error('Authentication failed');
      }
      if (response.status === 404) {
        return null; // No enrollment found
      }
      throw new Error('Failed to check enrollment status');
    }

    const enrollmentData = await response.json();

    // Cache the result
    setCachedData(SESSION_KEYS.ENROLLMENT, enrollmentData);

    // Also save to localStorage for backward compatibility
    if (enrollmentData) {
      await setLocalStorageWithSync(
        'gre_vocabulary_enrollment',
        JSON.stringify({
          isEnrolled: enrollmentData.is_enrolled,
          enrollmentDate: enrollmentData.enrollment_date,
          startDate: enrollmentData.start_date,
          durationDays: enrollmentData.duration_days,
          preferredTime: enrollmentData.preferred_time,
          timezone: enrollmentData.timezone,
        })
      );

      return enrollmentData;
    } else {
      localStorage.removeItem('gre_vocabulary_enrollment');
      return null;
    }
  } catch (error) {
    console.error('Error checking enrollment:', error);
    if (error.message === 'Authentication failed') {
      clearAllCaches();
    }
    throw error;
  }
};

const getDailyArticlesCached = async (userEmail) => {
  if (!userEmail) {
    throw new Error('User email not found');
  }

  if (!isTokenValid()) {
    return [];
  }

  // Check sessionStorage first
  if (
    isCacheValid(SESSION_KEYS.DAILY_ARTICLES, CACHE_DURATION.DAILY_ARTICLES)
  ) {
    const cachedData = getCachedData(SESSION_KEYS.DAILY_ARTICLES);
    if (cachedData) {
      return cachedData;
    }
  }

  // Make API call if no valid cache
  try {
    const windowResponse = await fetch(`${API_BASE_URL}/api/gre/program-window`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    if (windowResponse.ok) {
      const windowData = await windowResponse.json();
      const offset = Number(sessionStorage.getItem('gre_program_offset') || 0);
      const day = (windowData.days || []).find((item) => item.offset === offset);
      const articlesData = day?.items || [];
      setCachedData(SESSION_KEYS.DAILY_ARTICLES, articlesData);
      return articlesData;
    }

    const response = await fetch(`${API_BASE_URL}/api/gre/daily-articles`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token invalid, clear all caches
        clearAllCaches();
        throw new Error('Authentication failed');
      }
      throw new Error('Failed to fetch daily articles');
    }

    const articlesData = await response.json();

    // Store each article and its exercises separately in localStorage for easy access
    articlesData.forEach(async (article) => {
      await cacheGreArticleBundle(article);
    });

    // Cache the result in sessionStorage as well
    setCachedData(SESSION_KEYS.DAILY_ARTICLES, articlesData);

    return articlesData;
  } catch (error) {
    console.error('Error fetching daily articles:', error);
    if (error.message === 'Authentication failed') {
      clearAllCaches();
    }
    throw error;
  }
};

const DAYS_OPTIONS = [
  {
    days: 120,
    articlesPerDay: 2,
    totalArticles: 240,
    wordsPerDay: 10,
    months: 4,
  },
];

const FALLBACK_TIMEZONES = [
  'UTC',
  'Europe/Paris',
  'Europe/London',
  'Africa/Douala',
  'Africa/Lagos',
  'Africa/Abidjan',
  'Africa/Nairobi',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Toronto',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Kolkata',
];

const getSupportedTimezones = () => {
  try {
    if (
      typeof Intl !== 'undefined' &&
      typeof Intl.supportedValuesOf === 'function'
    ) {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (error) {
    console.warn('Unable to list timezones:', error);
  }
  return FALLBACK_TIMEZONES;
};

const formatTimezoneLabel = (timezoneId) => {
  if (!timezoneId) return 'UTC';
  const parts = timezoneId.split('/');
  const city = (parts[parts.length - 1] || timezoneId).replace(/_/g, ' ');
  return parts.length > 1 ? `${city} (${parts[0]})` : timezoneId;
};

const ENROLLMENT_TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
  // Time is stored in 24h format for backend
  const hh24 = String(hour).padStart(2, '0');

  // Label shown to user in 12h format (e.g. 12:00 AM, 6:00 AM, 1:00 PM)
  const isPM = hour >= 12;
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const label = `${hour12}:00 ${isPM ? 'PM' : 'AM'}`;

  const desc =
    hour <= 3 || hour >= 22
      ? 'Late Night'
      : hour <= 8
        ? 'Early Morning'
        : hour <= 11
          ? 'Morning'
          : hour <= 17
            ? 'Afternoon'
            : hour <= 20
              ? 'Evening'
              : 'Late Night';

  return {
    time: `${hh24}:00`,
    label,
    desc,
  };
});

// Helper function for other components to access Vocabulary Mastery progress data
export const getVocabularyMasteryProgressData = async (userEmail) => {
  try {
    const comprehensiveProgress =
      await getComprehensiveVerbalProgress(userEmail);
    return getVocabularyProgressFromComprehensive(comprehensiveProgress);
  } catch (error) {
    console.error('Error getting vocabulary mastery progress:', error);
    return {
      totalWords: 0,
      masteredWords: 0,
      accuracy: 0,
      streak: 0,
      lastUpdated: null,
      masteredWordsList: [],
      wordsNeedingPractice: [],
      weeklyProgress: {},
      monthlyProgress: {},
      recentArticles: [],
      completedArticlesCount: 0,
      masteryRate: 0,
      wordsInProgress: 0,
      wordsNeedReview: 0,
      targetWords: 800,
      completionPercentage: 0,
      averageWPM: 0,
      totalReadingTime: 0,
      articlesRead: 0,
    };
  }
};

export default function VocabularyMastery() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(() => {
    // Check if user is already enrolled
    const enrollment = JSON.parse(
      localStorage.getItem('gre_vocabulary_enrollment')
    );
    return enrollment?.isEnrolled ? 'progress' : 'intro';
  });
  const [daysSelected, setDaysSelected] = useState(120); // Default to 4 months
  const [preferredTime, setPreferredTime] = useState('09:00'); // Default to 9 AM
  const [timezone, setTimezone] = useState(''); // For backend (3-letter code)
  const [displayTimezone, setDisplayTimezone] = useState(''); // For frontend display
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [masteredWords, setMasteredWords] = useState([]);
  const [practiceWords, setPracticeWords] = useState([]);
  const [articles, setArticles] = useState([]);
  const [showVerbal155TargetHint, setShowVerbal155TargetHint] = useState(true);
  const [user, setUser] = useState(() => {
    try {
      const storedUser = sessionStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error('Error parsing user from sessionStorage:', error);
      return null;
    }
  });
  const [userEmail, setUserEmail] = useState(null);

  // New state for training plan integration
  const [hasTakenMockTest, setHasTakenMockTest] = useState(false);
  const [trainingPlanDuration, setTrainingPlanDuration] = useState(null);
  const [adjustedPlanDetails, setAdjustedPlanDetails] = useState(null);
  const [userSelectedDuration, setUserSelectedDuration] = useState(null);

  // State for detecting if user came from training plan modal
  const [fromTrainingPlan, setFromTrainingPlan] = useState(false);
  const [trainingPlanDurationWeeks, setTrainingPlanDurationWeeks] =
    useState(null);

  const [boostStatus, setBoostStatus] = useState(null);
  const [boostLoading, setBoostLoading] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const urlParams = new URLSearchParams(window.location.search);
  const redirectToLogin = useCallback(
    (message = 'Your session expired. Please sign in again.') => {
      clearAllCaches();
      navigate('/login', {
        replace: true,
        state: {
          redirectTo: `${location.pathname}${location.search}`,
          message,
        },
      });
    },
    [location.pathname, location.search, navigate]
  );

  // Helper function to check if user is enrolled
  const isUserEnrolled = () => {
    const enrollment = JSON.parse(
      localStorage.getItem('gre_vocabulary_enrollment')
    );
    return enrollment?.isEnrolled === true;
  };

  const timezoneOptions = useMemo(() => {
    const options = getSupportedTimezones();
    const current = timezone || displayTimezone;
    if (current && !options.includes(current)) {
      return [current, ...options];
    }
    return options;
  }, [timezone, displayTimezone]);

  const handleTimezoneChange = (event) => {
    const nextTimezone = event.target.value;
    setTimezone(nextTimezone);
    setDisplayTimezone(nextTimezone);
  };

  // Detect user's timezone on component mount
  useEffect(() => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setTimezone(userTimezone);
      setDisplayTimezone(userTimezone); // Display full timezone name (e.g., "Asia/Singapore", "America/New_York")
    } catch (error) {
      console.error('Error detecting timezone:', error);
      setTimezone('UTC'); // Fallback to UTC
      setDisplayTimezone('UTC');
    }
  }, []);

  // Auto-hide the 700+ words target hint after 10s (progress view only)
  useEffect(() => {
    if (step !== 'progress') return;
    setShowVerbal155TargetHint(true);
    const timer = window.setTimeout(() => {
      setShowVerbal155TargetHint(false);
    }, 10_000);
    return () => window.clearTimeout(timer);
  }, [step]);

  // Bootstrap the authenticated learner from session-backed storage only.
  useEffect(() => {
    if (!isTokenValid()) {
      setUserEmail(null);
      return;
    }

    if (user?.email) {
      setUserEmail(user.email);
      initializeSessionFromLocalStorage(user.email);
    } else {
      // Try localStorage as final fallback
      const storedEmail = localStorage.getItem('userEmail');
      if (storedEmail) {
        setUserEmail(storedEmail);
        initializeSessionFromLocalStorage(storedEmail);
      }
    }
  }, [user]);

  // Check training plan and mock test status on component mount
  useEffect(() => {
    const checkTrainingPlanStatus = async () => {
      if (!isTokenValid()) {
        return;
      }

      const mockTestTaken = hasUserTakenFirstMockTest(userEmail);
      setHasTakenMockTest(mockTestTaken);

      // Pass userEmail to check backend if localStorage is empty
      const durationMonths = await getTrainingPlanDurationMonths(
        userEmail,
        setToast
      );
      setTrainingPlanDuration(durationMonths);
      if (durationMonths) {
        setUserSelectedDuration(durationMonths);
      }

      if (mockTestTaken) {
        // Pass userEmail to check backend if localStorage is empty
        const durationMonths = await getTrainingPlanDurationMonths(
          userEmail,
          setToast
        );
        setTrainingPlanDuration(durationMonths);
        if (durationMonths) {
          setUserSelectedDuration(durationMonths);
          const planDetails = calculateArticlesPerDay(durationMonths);
          setAdjustedPlanDetails(planDetails);
        }
      }
    };

    if (userEmail) {
      checkTrainingPlanStatus();
    }
  }, [userEmail]);

  // Check if user came from training plan modal
  useEffect(() => {
    if (
      location.state?.fromTrainingPlan &&
      location.state?.trainingPlanDuration
    ) {
      setFromTrainingPlan(true);
      setTrainingPlanDurationWeeks(location.state.trainingPlanDuration);

      // Convert weeks to months for display
      const weeks = location.state.trainingPlanDuration;
      const months = Math.ceil(weeks / 4);
      setTrainingPlanDuration(months);
      setUserSelectedDuration(months);

      // Calculate adjusted plan details
      const planDetails = calculateArticlesPerDay(months);
      setAdjustedPlanDetails(planDetails);

      // Set hasTakenMockTest to true since they have a training plan
      setHasTakenMockTest(true);

      setStep('setup');
    }
  }, [location.state]);

  useEffect(() => {
    const initializeData = async () => {
      // Don't initialize data if userEmail is not available yet
      if (!userEmail) {
        setLoading(false);
        return;
      }

      if (!isTokenValid()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const enrollment = await checkEnrollmentCached(userEmail);
        if (enrollment?.is_enrolled) {
          const [comprehensiveProgress, articlesData] = await Promise.all([
            getComprehensiveVerbalProgress(userEmail),
            getDailyArticlesCached(userEmail),
          ]);

          // Transform comprehensive progress to expected format
          const progressData = getVocabularyProgressFromComprehensive(
            comprehensiveProgress
          );
          setProgress(progressData);
          setMasteredWords(progressData.masteredWordsList || []);
          setPracticeWords(progressData.wordsNeedingPractice || []);
          setArticles(articlesData);
          setPreferredTime(enrollment.preferred_time);
          setTimezone(enrollment.timezone);
          setDisplayTimezone(enrollment.timezone || 'UTC');
          if (enrollment.duration_days) {
            const enrolledMonths = Math.ceil(enrollment.duration_days / 30);
            setUserSelectedDuration(enrolledMonths);
          }
          if (!fromTrainingPlan && urlParams.get('auto_enroll') !== 'true') {
            setStep('progress');
          } else if (urlParams.get('auto_enroll') === 'true') {
            setStep('setup');
          }
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        setError(error.message);
        if (error.message === 'Authentication failed') {
          redirectToLogin();
        } else {
          // For other errors, stay on current step if user is enrolled
          if (!isUserEnrolled()) {
            setStep('intro');
          }
        }
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [redirectToLogin, userEmail]); // Add userEmail as dependency so it runs when userEmail is set

  // Clear cache when component unmounts or user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Keep cache on page refresh, only clear on navigation away from vocabulary
      if (!window.location.pathname.includes('/vocabulary')) {
        clearVocabularyCache();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Fetch boost status when userEmail is available
  useEffect(() => {
    const fetchBoostStatus = async () => {
      if (!userEmail || !isTokenValid()) return;

      try {
        const response = await fetchSharedBoostStatus(userEmail);
        if (response?.success) {
          setBoostStatus(response.boost_status);
        }
      } catch (error) {
        console.error('Error fetching boost status:', error);
        if (error.message === 'Authentication failed') {
          redirectToLogin();
        }
        // Don't set error state for boost status as it's not critical
      }
    };

    fetchBoostStatus();
  }, [redirectToLogin, userEmail]);

  // Update plan details when boost status changes
  useEffect(() => {
    if (boostStatus && adjustedPlanDetails && trainingPlanDuration) {
      // Calculate base plan details (without boost)
      const basePlanDetails = calculateArticlesPerDay(trainingPlanDuration);

      if (
        boostStatus.has_active_boost &&
        (trainingPlanDuration === 4 || trainingPlanDuration === 5)
      ) {
        // Apply boost multiplier with max 5 articles per day cap (only for 4 and 5 month plans)
        const boostedArticles = Math.min(
          basePlanDetails.articlesPerDay * boostStatus.boost_multiplier,
          5
        );
        const boostedWords = boostedArticles * 5; // 5 words per article

        const boostedPlanDetails = {
          ...basePlanDetails,
          articlesPerDay: boostedArticles,
          wordsPerDay: boostedWords,
        };
        setAdjustedPlanDetails(boostedPlanDetails);
      } else {
        // Use base plan without boost
        setAdjustedPlanDetails(basePlanDetails);
      }
    }
  }, [boostStatus, trainingPlanDuration]);

  // Handle enrollment setup from URL parameters
  useEffect(() => {
    const handleEnrollmentSetup = () => {
      const autoEnroll = urlParams.get('auto_enroll');
      const duration = urlParams.get('duration');
      const time = urlParams.get('time');
      const urlTimezone = urlParams.get('timezone');

      // Check if user is trying to access auto-enroll link
      if (autoEnroll === 'true') {
        // Check if user is logged in
        if (!user || !userEmail) {
          // User not logged in - redirect to login with return URL
          const currentUrl = window.location.pathname + window.location.search;
          navigate('/login', {
            state: {
              redirectTo: currentUrl,
              message: 'Please login to enroll in the vocabulary program',
            },
          });
          return;
        }

        // Check if user is premium
        if (!user.is_premium) {
          toastdisplay(
            'Premium subscription required to access vocabulary program. Please upgrade to continue.',
            false,
            5000,
            setToast
          );
          // Redirect to pricing page after showing message
          setTimeout(() => {
            navigate('/pricing');
          }, 3000);
          return;
        }

        // Check if user is already enrolled
        if (!isUserEnrolled()) {
          toastdisplay(
            'You are already enrolled in the vocabulary program!',
            true,
            3000,
            setToast
          );
          setStep('progress');
          return;
        }

        // User is logged in, premium, and not enrolled - show setup screen with pre-filled values
        const enrollmentDuration = duration ? parseInt(duration) : 120;
        const enrollmentTime = time ? decodeURIComponent(time) : '09:00';
        const enrollmentTimezone = urlTimezone || timezone || 'UTC';

        // Update state with URL parameters
        setDaysSelected(enrollmentDuration);
        setPreferredTime(enrollmentTime);
        if (urlTimezone) {
          setTimezone(enrollmentTimezone);
          setDisplayTimezone(enrollmentTimezone);
        }

        // Calculate plan details if we have duration
        if (enrollmentDuration) {
          const months = Math.ceil(enrollmentDuration / 30);
          const planDetails = calculateArticlesPerDay(months);
          setAdjustedPlanDetails(planDetails);
          setTrainingPlanDuration(months);
          setUserSelectedDuration(months);
        }

        // Show setup screen
        setStep('setup');

        // Show informational message
        toastdisplay(
          'Please confirm your enrollment preferences and click "Enroll in Vocabulary Program"',
          true,
          4000,
          setToast
        );
      }
    };

    // Run when component mounts or when user/timezone changes
    if (timezone) {
      handleEnrollmentSetup();
    }
  }, [user, userEmail, timezone, navigate]); // Run when user, userEmail, or timezone becomes available

  const handleEnroll = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!isTokenValid()) {
        clearAllCaches();
        throw new Error('Authentication failed');
      }

      // Use user-selected duration (from slider) if available, otherwise fall back
      // to training plan duration, then default daysSelected
      let enrollmentDuration = daysSelected;

      if (userSelectedDuration) {
        enrollmentDuration = userSelectedDuration * 30;
      } else {
        const existingTrainingPlan = localStorage.getItem('liwaza-training-plan');
        if (existingTrainingPlan) {
          try {
            const parsed = JSON.parse(existingTrainingPlan);
            if (parsed && parsed.duration_weeks) {
              let planDurationDays;
              if (parsed.duration_weeks === 20) {
                planDurationDays = 150;
              } else if (parsed.duration_weeks === 16) {
                planDurationDays = 120;
              } else if (parsed.duration_weeks === 12) {
                planDurationDays = 90;
              } else if (parsed.duration_weeks === 8) {
                planDurationDays = 60;
              } else if (parsed.duration_weeks === 4) {
                planDurationDays = 30;
              } else {
                planDurationDays = parsed.duration_weeks * 7;
              }
              enrollmentDuration = planDurationDays;
            }
          } catch (error) {
            console.error('Error parsing training plan:', error);
          }
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/gre/enroll`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          duration_days: enrollmentDuration,
          preferred_time: preferredTime,
          timezone: timezone,
        }),
      });
      setFromTrainingPlan(false);

      if (!response.ok) {
        if (response.status === 401) {
          clearAllCaches();
          throw new Error('Authentication failed');
        }
        throw new Error('Failed to enroll user');
      }

      const enrollmentData = await response.json();

      // Immediately save enrollment data to localStorage (primary storage)
      const enrollmentCache = {
        isEnrolled: true, // Explicitly set to true since enrollment was successful
        enrollmentDate:
          enrollmentData.enrollment?.enrollment_date ||
          new Date().toISOString(),
        startDate:
          enrollmentData.enrollment?.start_date || enrollmentData.start_date,
        durationDays: enrollmentDuration, // Use the actual duration used for enrollment
        preferredTime: preferredTime,
        timezone: timezone,
        userId:
          enrollmentData.enrollment?.userEmail || enrollmentData.user_email,
        enrollmentId: enrollmentData.enrollment?.id,
      };

      // Store in localStorage immediately
      await setLocalStorageWithSync(
        'gre_vocabulary_enrollment',
        JSON.stringify(enrollmentCache)
      );

      // Enhance enrollmentData with is_enrolled field before caching
      const enhancedEnrollmentData = {
        ...enrollmentData,
        is_enrolled: true,
        enrollment_date:
          enrollmentData.enrollment?.enrollment_date ||
          new Date().toISOString(),
        start_date:
          enrollmentData.enrollment?.start_date || enrollmentData.start_date,
        duration_days: enrollmentDuration,
        preferred_time: preferredTime,
        timezone: timezone,
      };

      // Cache in sessionStorage as well for performance
      setCachedData(SESSION_KEYS.ENROLLMENT, enhancedEnrollmentData);

      // Set initial articles from enrollment response and cache them along with their exercises
      if (enrollmentData.initial_articles) {
        setArticles(enrollmentData.initial_articles);
        setCachedData(
          SESSION_KEYS.DAILY_ARTICLES,
          enrollmentData.initial_articles
        );

        // Cache each article and its exercises separately for individual access
        enrollmentData.initial_articles.forEach(async (article) => {
          await cacheGreArticleBundle(article);
        });
      }

      // Get initial progress data
      try {
        const comprehensiveProgress =
          await getCachedComprehensiveProgress(userEmail);
        const progressData = getVocabularyProgressFromComprehensive(
          comprehensiveProgress
        );
        setProgress(progressData);

        // Clear verbal progress cache to ensure fresh data for next request
        clearVerbalProgressCache(userEmail);
      } catch (progressError) {
        console.error('Error fetching progress data:', progressError);
        // Don't fail enrollment if progress fetch fails
      }

      setStep('confirmation');
    } catch (error) {
      setError(error.message);
      if (error.message === 'Authentication failed') {
        // Handle auth error appropriately
        clearAllCaches();
        // Only redirect to intro if user was not previously enrolled
        if (!isUserEnrolled()) {
          setStep('intro');
        }
        // If user is enrolled, stay on current step and let them retry
      }
    } finally {
      setLoading(false);
    }
  };

  // Boost handlers
  const handleEnableBoost = async () => {
    if (!userEmail) return;

    try {
      setBoostLoading(true);
      const response = await enableArticleBoost(userEmail);

      if (response.success) {
        setBoostStatus(response.boost_status);

        // Clear articles cache and reload fresh articles from backend
        clearVocabularyCache();
        try {
          const freshArticles = await getDailyArticlesCached(userEmail);
          setArticles(freshArticles);
        } catch (articleError) {
          console.error('Error reloading articles after boost:', articleError);
        }

        // Show success message
        toastdisplay(response.message, true, 3000, setToast);
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        toastdisplay(
          'Failed to enable boost: ' + response.message,
          false,
          3000,
          setToast
        );
      }
    } catch (error) {
      console.error('Error enabling boost:', error);
      toastdisplay(
        'Failed to enable boost. Please try again.',
        false,
        3000,
        setToast
      );
    } finally {
      setBoostLoading(false);
    }
  };

  const handleDeactivateBoost = async () => {
    if (!userEmail) return;

    try {
      setBoostLoading(true);
      const response = await deactivateArticleBoost(userEmail);

      if (response.success) {
        setBoostStatus(response.boost_status);

        // Clear articles cache and reload fresh articles from backend
        clearVocabularyCache();
        try {
          const freshArticles = await getDailyArticlesCached(userEmail);
          setArticles(freshArticles);
        } catch (articleError) {
          console.error(
            'Error reloading articles after boost deactivation:',
            articleError
          );
        }

        // Show success message
        toastdisplay(response.message, true, 3000, setToast);
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        toastdisplay(
          'Failed to deactivate boost: ' + response.message,
          false,
          3000,
          setToast
        );
      }
    } catch (error) {
      console.error('Error deactivating boost:', error);
      toastdisplay(
        'Failed to deactivate boost. Please try again.',
        false,
        3000,
        setToast
      );
    } finally {
      setBoostLoading(false);
    }
  };

  const hasAccessedGreBook = () => {
    try {
      return localStorage.getItem(`gre_book_accessed_${userEmail}`) === 'true';
    } catch {
      return false;
    }
  };

  const markGreBookAccessed = () => {
    try {
      localStorage.setItem(`gre_book_accessed_${userEmail}`, 'true');
    } catch {}
  };

  const renderLiwazaGREBook = () => {
    const alreadyAccessed = hasAccessedGreBook();

    if (alreadyAccessed) {
      return (
        <div className="liwaza_gre_book_section">
          <div className="liwaza_gre_book_card collapsed">
            <div className="liwaza_gre_book_header">
              <div className="liwaza_gre_book_icon">
                <FiBook />
              </div>
              <h2 className="liwaza_gre_book_title">Liwaza GRE Book</h2>
            </div>
            <div className="liwaza_gre_book_actions">
              <button
                className="liwaza_gre_book_btn primary"
                onClick={() => navigate('/exams/gre/verbal/vocabulary-book')}
              >
                <FiBook />
                Open
              </button>
              <button
                className="liwaza_gre_book_btn secondary"
                onClick={() =>
                  navigate('/exams/gre/verbal/vocabulary-book?export=pdf')
                }
              >
                <FiDownload />
                PDF
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="liwaza_gre_book_section">
        <div className="liwaza_gre_book_card">
          <div className="liwaza_gre_book_header">
            <div className="liwaza_gre_book_icon">
              <FiBook />
            </div>
            <div className="liwaza_gre_book_title_container">
              <h2 className="liwaza_gre_book_title">Liwaza GRE Book</h2>
              <p className="liwaza_gre_book_subtitle">
                Your complete GRE vocabulary reference
              </p>
            </div>
          </div>

          <p className="liwaza_gre_book_description">
            Access the complete Liwaza GRE vocabulary collection with{' '}
            <strong>1000+ essential words</strong>. Browse, search, and study
            all words with definitions, synonyms, examples, flashcards, and
            quizzes.
          </p>

          <div className="liwaza_gre_book_features">
            <div className="liwaza_gre_book_feature">
              <FiLayers />
              <span>1000+ words</span>
            </div>
            <div className="liwaza_gre_book_feature">
              <FiTarget />
              <span>Flashcards & Quiz</span>
            </div>
            <div className="liwaza_gre_book_feature">
              <FiTrendingUp />
              <span>Track progress</span>
            </div>
            <div className="liwaza_gre_book_feature">
              <FiDownload />
              <span>Export PDF</span>
            </div>
          </div>

          <div className="liwaza_gre_book_actions">
            <button
              className="liwaza_gre_book_btn primary"
              onClick={() => {
                markGreBookAccessed();
                navigate('/exams/gre/verbal/vocabulary-book');
              }}
            >
              <FiBook />
              Open GRE Book
            </button>
            <button
              className="liwaza_gre_book_btn secondary"
              onClick={() => {
                markGreBookAccessed();
                navigate('/exams/gre/verbal/vocabulary-book?export=pdf');
              }}
            >
              <FiDownload />
              Export PDF
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderIntro = () => (
    <div className="vocabulary_intro">
      {/* Liwaza GRE Book - Always visible first */}
      {renderLiwazaGREBook()}

      <div className="vocabulary_intro_header">
        <h1 className="vocabulary_intro_title">
          Master 1000+ Essential GRE Words
        </h1>
        <p className="vocabulary_intro_subtitle">
          Using Liwaza's proven contextual learning method
        </p>
      </div>

      <div className="vocabulary_score_hint">
        <FiTarget size={20} className="vocabulary_score_hint_icon" />
        <div className="vocabulary_score_hint_content">
          <div className="vocabulary_score_hint_title">
            GRE Verbal 155+ target
          </div>
          <div className="vocabulary_score_hint_text">
            Master at least <strong>700 words</strong> out of the{' '}
            <strong>1362</strong> GRE words to already aim for{' '}
            <strong>155+</strong>.
          </div>
        </div>
      </div>

      <div className="vocabulary_features_grid">
        <div className="vocabulary_feature_card">
          <FiMail size={24} className="vocabulary_feature_icon" />
          <h3 className="vocabulary_feature_title">Daily Articles</h3>
          <p className="vocabulary_feature_text">
            Receive 2-3 GRE-level articles every morning, carefully curated for
            vocabulary enrichment
          </p>
        </div>

        <div className="vocabulary_feature_card">
          <FiClock size={24} className="vocabulary_feature_icon" />
          <h3 className="vocabulary_feature_title">30 Minutes Daily</h3>
          <p className="vocabulary_feature_text">
            Just 30 minutes of focused learning per day to master new vocabulary
            in context
          </p>
        </div>

        <div className="vocabulary_feature_card">
          <FiCheck size={24} className="vocabulary_feature_icon" />
          <h3 className="vocabulary_feature_title">Proven Method</h3>
          <p className="vocabulary_feature_text">
            Learn and retain vocabulary through contextual understanding and
            daily practice
          </p>
        </div>
      </div>

      <GetStartedWith
        title="Don't understand something?"
        spaceName="Verbal vault"
        humanCoachTitle="Talk to a GRE coach"
        liwazaAITitle="Ask AI"
        communityTitle="Ask other students"
        aifirstmessage="I need help understanding the Liwaza method for learning GRE vocabulary"
        aicontext={`understanding the Liwaza method for learning GRE vocabulary: read daily articles and master words associated while doing the exercises related`}
        aiOnly={true}
      />

      <div className="vocabulary_intro_cta">
        <button
          onClick={() => setStep('setup')}
          className="vocabulary_get_started_btn"
        >
          Get Started <FiArrowRight />
        </button>
      </div>
    </div>
  );

  const renderSetup = () => (
    <div className="gre-layout">
      <Sidebar />
      <main className="gre-main">
        {/* Liwaza GRE Book - Always visible */}
        {renderLiwazaGREBook()}

        <div className="vocabulary_setup">
          <h2 className="vocabulary_setup_title">
            {fromTrainingPlan
              ? 'Enroll in Your Vocabulary Program'
              : 'Choose Your Learning Timeline'}
          </h2>

          <p className="vocabulary_setup_subtitle">
            {fromTrainingPlan
              ? 'Choose when you want to receive your daily vocabulary articles. Each article is carefully curated to help you master 5 essential GRE words.'
              : 'Select a schedule that fits your preparation timeline. Each article is carefully curated to help you master 5 essential GRE words.'}
          </p>

          <div className="vocabulary_setup_form">
            {/* Show adjusted plan if user has taken mock test */}
            {hasTakenMockTest &&
              trainingPlanDuration &&
              adjustedPlanDetails && (
                <div className="vocabulary_adjusted_plan_info">
                  <FiTarget size={20} />
                  <div className="vocabulary_plan_details">
                    <span className="vocabulary_plan_duration">
                      {trainingPlanDuration}-Month Personalized Plan Available
                    </span>
                    <span className="vocabulary_plan_articles">
                      {adjustedPlanDetails.articlesPerDay} articles/day •{' '}
                      {adjustedPlanDetails.wordsPerDay} words/day
                    </span>
                  </div>
                </div>
              )}

            {/* Special message when user comes from training plan modal */}
            {fromTrainingPlan && trainingPlanDurationWeeks && (
              <div className="vocabulary_training_plan_message">
                <FiInfo size={20} className="vocabulary_message_icon" />
                <div className="vocabulary_message_content">
                  <h4 className="vocabulary_message_title">
                    <FiTarget size={20} /> Adjust Your Vocabulary Plan Duration
                  </h4>
                  <p className="vocabulary_message_text">
                    You have a {trainingPlanDurationWeeks}-week GRE training
                    plan. You can now adjust your vocabulary article delivery
                    schedule to match this timeline. Your current plan:{' '}
                    <strong>
                      {adjustedPlanDetails?.articlesPerDay} articles per day
                    </strong>{' '}
                    for <strong>{trainingPlanDuration} months</strong>.
                  </p>
                </div>
              </div>
            )}

            {!hasTakenMockTest &&
              !trainingPlanDuration &&
              !adjustedPlanDetails && (
                <>
                  <div
                    className="vocabulary_setup_option"
                    style={{ background: 'var(--liwaza-primary-container)' }}
                  >
                    <h3 className="vocabulary_option_title">
                      <FiTarget size={20} /> Option 1: Take GRE Mock Test First
                    </h3>
                    <p className="vocabulary_option_description">
                      Your plan duration will be automatically adjusted based on
                      your first GRE mock test results. This ensures you get the
                      optimal study timeline for your skill level.
                    </p>
                    <button
                      onClick={() => navigate('/exams/gre/full/full-test-1')}
                      className="vocabulary_take_mock_test_btn"
                    >
                      <FiPlay size={16} /> Take GRE Mock Test Now
                    </button>
                  </div>

                  {/* OR Separator */}
                  <div className="vocabulary_or_separator">
                    <div className="vocabulary_or_line"></div>
                    <span className="vocabulary_or_text">OR</span>
                    <div className="vocabulary_or_line"></div>
                  </div>
                </>
              )}
            {/* Option 2: Default Enrollment Plan */}
            <div className="vocabulary_setup_option">
              {!hasTakenMockTest &&
                !trainingPlanDuration &&
                !adjustedPlanDetails && (
                  <>
                    <h3 className="vocabulary_option_title">
                      <FiBookOpen size={20} /> Option 2: Start with{' '}
                      {hasTakenMockTest && trainingPlanDuration
                        ? 'Personalized'
                        : 'Default'}{' '}
                      Plan
                    </h3>
                    <p className="vocabulary_option_description">
                      'Begin your vocabulary journey immediately with our proven
                      4-month plan. Your plan will be automatically adjusted and
                      optimized once you take your first GRE mock test.'
                    </p>
                  </>
                )}

              <div className="vocabulary_setup_section">
                <label className="vocabulary_setup_label">
                  {hasTakenMockTest && trainingPlanDuration
                    ? `Your ${(userSelectedDuration || trainingPlanDuration) > trainingPlanDuration ? 'Extended' : 'Adjusted'} ${userSelectedDuration || trainingPlanDuration}-Month Personalized Vocabulary Plan`
                    : `Your ${userSelectedDuration && userSelectedDuration > 4 ? 'Extended' : 'Default'} ${userSelectedDuration || 4}-Month Vocabulary Plan`}
                </label>
                <div className="vocabulary_days_grid">
                  {hasTakenMockTest &&
                  trainingPlanDuration &&
                  adjustedPlanDetails ? (
                    // Show personalized plan details
                    <div className="vocabulary_days_option selected">
                      <div className="vocabulary_days_title">
                        <FiCalendar /> {userSelectedDuration || trainingPlanDuration} Months (
                        {(userSelectedDuration || trainingPlanDuration) * 30} Days)
                      </div>
                      <div className="vocabulary_days_details">
                        {(trainingPlanDuration === 4 ||
                          trainingPlanDuration === 5) &&
                        boostStatus?.has_active_boost ? (
                          <>
                            <div
                              className="vocabulary_days_detail"
                              style={{
                                color: 'var(--liwaza-primary)',
                                fontWeight: 'bold',
                              }}
                            >
                              <FiZap /> {adjustedPlanDetails.articlesPerDay}{' '}
                              articles per day (2x BOOSTED)
                            </div>
                            <div
                              className="vocabulary_days_detail"
                              style={{
                                color: 'var(--liwaza-primary)',
                                fontWeight: 'bold',
                              }}
                            >
                              <FiBook /> {adjustedPlanDetails.wordsPerDay} words
                              daily (BOOSTED)
                            </div>
                            <div
                              className="vocabulary_days_detail"
                              style={{ fontSize: '12px', color: '#666' }}
                            >
                              <FiClock /> After {boostStatus.days_remaining}{' '}
                              days:{' '}
                              {
                                calculateArticlesPerDay(trainingPlanDuration)
                                  .articlesPerDay
                              }{' '}
                              articles/day normal
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="vocabulary_days_detail">
                              <FiBookOpen />{' '}
                              {adjustedPlanDetails.articlesPerDay} articles per
                              day
                            </div>
                            <div className="vocabulary_days_detail">
                              <FiBook /> {adjustedPlanDetails.wordsPerDay} new
                              words daily
                            </div>
                            <div className="vocabulary_days_detail">
                              <FiTrendingUp />{' '}
                              {adjustedPlanDetails.articlesPerDay * (userSelectedDuration || trainingPlanDuration) * 30 * 5} words
                              mastered
                            </div>
                          </>
                        )}
                      </div>
                      <div className="vocabulary_days_note">
                        {(trainingPlanDuration === 4 ||
                          trainingPlanDuration === 5) &&
                        boostStatus?.has_active_boost ? (
                          <>
                            <FiZap
                              size={14}
                              style={{ color: 'var(--liwaza-primary)' }}
                            />
                            <span style={{ color: 'var(--liwaza-primary)' }}>
                              2x Boost active for {boostStatus.days_remaining}{' '}
                              more days
                            </span>
                          </>
                        ) : (
                          <>
                            <FiCheck size={14} />{' '}
                            {(userSelectedDuration || trainingPlanDuration) > trainingPlanDuration
                              ? `Extended from ${trainingPlanDuration} to ${userSelectedDuration}-month plan`
                              : `Using your personalized ${trainingPlanDuration}-month plan`}
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Show default plan option with user-adjustable duration
                    (() => {
                      const option = DAYS_OPTIONS[0];
                      const effectiveDuration = userSelectedDuration || option.months;
                      return (
                        <div className="vocabulary_days_option selected">
                          <div className="vocabulary_days_title">
                            <FiCalendar /> {effectiveDuration} Months ({effectiveDuration * 30} Days)
                          </div>
                          <div className="vocabulary_days_details">
                            <div className="vocabulary_days_detail">
                              <FiBookOpen /> {option.articlesPerDay} articles per day
                            </div>
                            <div className="vocabulary_days_detail">
                              <FiBook /> {option.wordsPerDay} new words daily
                            </div>
                            <div className="vocabulary_days_detail">
                              <FiTrendingUp /> {option.articlesPerDay * effectiveDuration * 30 * 5} words mastered
                            </div>
                          </div>
                          <div className="vocabulary_days_note">
                            <FiInfo size={14} />{' '}
                            {effectiveDuration > option.months
                              ? `Extended from ${option.months} to ${effectiveDuration} months`
                              : 'Plan will be optimized after your first mock test'}
                          </div>
                        </div>
                      );
                    })()
                  )}
                </div>
              </div>

              <div className="vocabulary_setup_section">
                <label className="vocabulary_setup_label">
                  Want to train longer?
                </label>
                <p className="vocabulary_duration_slider_description">
                  Extend your plan duration beyond the recommended{' '}
                  <strong>{trainingPlanDuration || 4} months</strong>. The daily
                  pace stays the same &mdash; you simply train for more days.
                </p>
                <div className="vocabulary_duration_slider_container">
                  <div className="vocabulary_duration_slider_labels">
                    <span>{trainingPlanDuration || 4} mo</span>
                    <span>12 mo</span>
                  </div>
                  <input
                    type="range"
                    className="vocabulary_duration_slider"
                    min={trainingPlanDuration || 4}
                    max={12}
                    step={1}
                    value={userSelectedDuration || trainingPlanDuration || 4}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      setUserSelectedDuration(val);
                      if (!hasTakenMockTest || !trainingPlanDuration) {
                        setDaysSelected(val * 30);
                      }
                    }}
                  />
                  <div className="vocabulary_duration_slider_value">
                    <FiCalendar size={16} />
                    <span>
                      {userSelectedDuration || trainingPlanDuration || 4} months
                      ({(userSelectedDuration || trainingPlanDuration || 4) * 30} days)
                    </span>
                    {(userSelectedDuration || trainingPlanDuration || 4) > (trainingPlanDuration || 4) && (
                      <span className="vocabulary_duration_extended_badge">
                        +{(userSelectedDuration || trainingPlanDuration || 4) - (trainingPlanDuration || 4)} months extended
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="vocabulary_setup_section">
                <label className="vocabulary_setup_label">
                  When would you like to receive your daily articles?
                </label>

                <div className="vocabulary_time_container">
                  <div className="vocabulary_timezone_info">
                    <FiGlobe aria-hidden="true" />
                    <label
                      htmlFor="vocabulary-timezone"
                      className="vocabulary_timezone_label"
                    >
                      Your timezone
                    </label>
                    <select
                      id="vocabulary-timezone"
                      className="vocabulary_timezone_select"
                      value={timezone || displayTimezone || 'UTC'}
                      onChange={handleTimezoneChange}
                    >
                      {timezoneOptions.map((tz) => (
                        <option key={tz} value={tz}>
                          {formatTimezoneLabel(tz)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="vocabulary_time_grid">
                    {ENROLLMENT_TIME_OPTIONS.map((timeOption) => (
                      <button
                        key={timeOption.time}
                        onClick={() => setPreferredTime(timeOption.time)}
                        className={`vocabulary_time_option ${preferredTime === timeOption.time ? 'selected' : ''}`}
                      >
                        <div className="vocabulary_time_label">
                          {timeOption.label}
                        </div>
                        <div className="vocabulary_time_desc">
                          {timeOption.desc}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {error && (
                <div className="vocabulary_error_message">
                  {getErrorMessage(error)}
                </div>
              )}

              <div className="vocabulary_schedule_summary">
                <p className="vocabulary_schedule_title">
                  {hasTakenMockTest && trainingPlanDuration
                    ? 'Your Personalized Schedule'
                    : 'Your Schedule'}
                </p>
                <p className="vocabulary_schedule_text">
                  {hasTakenMockTest &&
                  trainingPlanDuration &&
                  adjustedPlanDetails ? (
                    <>
                      {(trainingPlanDuration === 4 ||
                        trainingPlanDuration === 5) &&
                      boostStatus?.has_active_boost ? (
                        <>
                          <strong style={{ color: 'var(--liwaza-primary)' }}>
                            <FiZap size={16} style={{ marginRight: '4px' }} />
                            BOOSTED: {adjustedPlanDetails.articlesPerDay}{' '}
                            articles daily
                          </strong>{' '}
                          from our bank of 200+ curated articles for{' '}
                          <strong>{userSelectedDuration || trainingPlanDuration} months</strong>.
                          Master{' '}
                          <strong style={{ color: 'var(--liwaza-primary)' }}>
                            {adjustedPlanDetails.wordsPerDay} words every day
                          </strong>{' '}
                          during boost period!
                          <br />
                          <span style={{ color: '#666', fontSize: '14px' }}>
                            After boost expires ({boostStatus.days_remaining}{' '}
                            days remaining), you'll receive{' '}
                            {
                              calculateArticlesPerDay(trainingPlanDuration)
                                .articlesPerDay
                            }{' '}
                            articles daily (
                            {
                              calculateArticlesPerDay(trainingPlanDuration)
                                .wordsPerDay
                            }{' '}
                            words/day).
                          </span>
                        </>
                      ) : (
                        <>
                          {adjustedPlanDetails.articlesPerDay} articles daily
                          from our bank of 200+ curated articles for{' '}
                          <strong>{userSelectedDuration || trainingPlanDuration} months</strong>.
                          Master {adjustedPlanDetails.wordsPerDay} words every day!
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {DAYS_OPTIONS[0].articlesPerDay} articles daily from our
                      bank of 200+ curated articles for{' '}
                      <strong>{userSelectedDuration || 4} months</strong>.
                      Master {DAYS_OPTIONS[0].wordsPerDay} words every day!
                    </>
                  )}
                </p>
                <p className="vocabulary_schedule_time">
                  <FiClock />
                  Daily articles delivered at{' '}
                  {new Date(`2000-01-01T${preferredTime}`).toLocaleTimeString(
                    [],
                    {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    }
                  )}{' '}
                  ({displayTimezone})
                </p>
                {hasTakenMockTest &&
                trainingPlanDuration &&
                adjustedPlanDetails ? (
                  <div className="vocabulary_schedule_note">
                    {(trainingPlanDuration === 4 ||
                      trainingPlanDuration === 5) &&
                    boostStatus?.has_active_boost ? (
                      <>
                        <FiZap
                          size={16}
                          style={{ color: 'var(--liwaza-primary)' }}
                        />
                        <span>
                          <strong style={{ color: 'var(--liwaza-primary)' }}>
                            Boost Active!
                          </strong>
                          You're currently receiving{' '}
                          {adjustedPlanDetails.articlesPerDay} articles daily
                          (2x boosted) for {boostStatus.days_remaining} more
                          days. After that, you'll get the normal{' '}
                          {
                            calculateArticlesPerDay(trainingPlanDuration)
                              .articlesPerDay
                          }{' '}
                          articles daily for the remaining{' '}
                          {userSelectedDuration || trainingPlanDuration} months.
                        </span>
                      </>
                    ) : (
                      <>
                        <FiCheck size={16} />
                        <span>
                          <strong>Great!</strong> Your plan is personalized
                          based on your GRE mock test results. You'll receive{' '}
                          {adjustedPlanDetails.articlesPerDay} articles daily
                          for {userSelectedDuration || trainingPlanDuration} months, mastering{' '}
                          {adjustedPlanDetails.wordsPerDay} new words each day.
                        </span>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="vocabulary_schedule_note">
                    <FiInfo size={16} />
                    <span>
                      <strong>Note:</strong> This is your starting plan. Once
                      you take a GRE mock test, your plan will be automatically
                      optimized based on your performance level.
                    </span>
                  </div>
                )}
              </div>

              <button
                onClick={handleEnroll}
                disabled={loading}
                className="vocabulary_enroll_btn"
              >
                {loading
                  ? 'Enrolling...'
                  : fromTrainingPlan
                    ? 'Enroll in Vocabulary Program'
                    : hasTakenMockTest && trainingPlanDuration
                      ? 'Start Personalized Journey'
                      : 'Start My Journey'}{' '}
                {!loading && <FiArrowRight />}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );

  const renderConfirmation = () => (
    <div className="vocabulary_confirmation">
      {/* Liwaza GRE Book - Always visible */}
      {renderLiwazaGREBook()}

      <div className="vocabulary_success_banner">
        <FiCheck size={48} style={{ marginBottom: '1rem' }} />
        <h2 className="vocabulary_success_title">
          Welcome to Vocabulary Mastery!
        </h2>
        <p className="vocabulary_success_text">
          You're enrolled in the{' '}
          {hasTakenMockTest && trainingPlanDuration
            ? `${userSelectedDuration || trainingPlanDuration}-month personalized`
            : `${userSelectedDuration || DAYS_OPTIONS.find((opt) => opt.days === daysSelected)?.months}-month`}{' '}
          program
        </p>
      </div>

      {/* Mock Test Encouragement Section - Only show if user hasn't taken mock test */}
      {!hasTakenMockTest ? (
        <div className="vocabulary_mock_test_encouragement">
          <h3 className="vocabulary_encouragement_title">
            <FiTarget size={24} /> Next Step: Take Your First GRE Mock Test
          </h3>
          <p className="vocabulary_encouragement_text">
            To optimize your vocabulary learning plan, we need to understand
            your current GRE performance level. Your plan duration will be
            automatically adjusted based on your results.
          </p>
          <div className="vocabulary_encouragement_benefits">
            <div className="vocabulary_benefit_item">
              <FiTrendingUp size={20} />
              <span>Personalized study timeline</span>
            </div>
            <div className="vocabulary_benefit_item">
              <FiZap size={20} />
              <span>Optimized daily article count</span>
            </div>
            <div className="vocabulary_benefit_item">
              <FiTarget size={20} />
              <span>Targeted vocabulary focus</span>
            </div>
          </div>
          <button
            onClick={() =>
              navigate('/exams/gre', { state: { activeCategory: 'fulltests' } })
            }
            className="vocabulary_take_mock_test_btn primary"
          >
            <FiPlay size={16} /> Take GRE Mock Test Now
          </button>
        </div>
      ) : (
        <div className="vocabulary_mock_test_encouragement">
          <h3 className="vocabulary_encouragement_title">
            <FiCheck size={24} /> Your Plan is Now Optimized!
          </h3>
          <p className="vocabulary_encouragement_text">
            Great news! You've taken a GRE mock test, so your vocabulary plan
            has been personalized to match your current skill level and study
            timeline.
          </p>
          <div className="vocabulary_encouragement_benefits">
            <div className="vocabulary_benefit_item">
              <FiTarget size={20} />
              <span>{userSelectedDuration || trainingPlanDuration}-month personalized timeline</span>
            </div>
            <div className="vocabulary_benefit_item">
              {(trainingPlanDuration === 4 || trainingPlanDuration === 5) &&
              boostStatus?.has_active_boost ? (
                <FiZap size={20} style={{ color: 'var(--liwaza-primary)' }} />
              ) : (
                <FiBookOpen size={20} />
              )}
              <span>
                {(trainingPlanDuration === 4 || trainingPlanDuration === 5) &&
                boostStatus?.has_active_boost ? (
                  <>
                    <strong style={{ color: 'var(--liwaza-primary)' }}>
                      {adjustedPlanDetails?.articlesPerDay} articles daily (2x
                      BOOSTED)
                    </strong>
                    <br />
                    <small style={{ color: '#666' }}>
                      Ends in {boostStatus.days_remaining} days
                      <br />
                      Then{' '}
                      {
                        calculateArticlesPerDay(trainingPlanDuration)
                          .articlesPerDay
                      }{' '}
                      articles/day
                    </small>
                  </>
                ) : (
                  `${adjustedPlanDetails?.articlesPerDay} articles daily`
                )}
              </span>
            </div>
            <div className="vocabulary_benefit_item">
              <FiTrendingUp size={20} />
              <span>Optimized for your skill level</span>
            </div>
          </div>
        </div>
      )}

      <div className="vocabulary_whats_next">
        <h3 className="vocabulary_whats_next_title">What's Next?</h3>
        <div className="vocabulary_next_grid">
          <div className="vocabulary_next_item primary">
            <FiMail size={24} className="vocabulary_next_icon primary" />
            <p className="vocabulary_next_text primary">
              Daily emails with curated articles
            </p>
          </div>
          <div className="vocabulary_next_item secondary">
            {(trainingPlanDuration === 4 || trainingPlanDuration === 5) &&
            boostStatus?.has_active_boost ? (
              <FiZap
                size={24}
                className="vocabulary_next_icon secondary"
                style={{ color: 'var(--liwaza-primary)' }}
              />
            ) : (
              <FiBookOpen
                size={24}
                className="vocabulary_next_icon secondary"
              />
            )}
            <p className="vocabulary_next_text secondary">
              {(trainingPlanDuration === 4 || trainingPlanDuration === 5) &&
              boostStatus?.has_active_boost ? (
                <>
                  <strong style={{ color: 'var(--liwaza-primary)' }}>
                    {adjustedPlanDetails.articlesPerDay} articles per day (2x
                    BOOSTED)
                  </strong>
                  <br />
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    Boost ends in {boostStatus.days_remaining} days
                    <br />
                    Then{' '}
                    {
                      calculateArticlesPerDay(trainingPlanDuration)
                        .articlesPerDay
                    }{' '}
                    articles/day normal
                  </span>
                </>
              ) : (
                <>
                  {hasTakenMockTest &&
                  trainingPlanDuration &&
                  adjustedPlanDetails
                    ? adjustedPlanDetails.articlesPerDay
                    : DAYS_OPTIONS.find((opt) => opt.days === daysSelected)
                        ?.articlesPerDay}{' '}
                  articles per day
                </>
              )}
            </p>
          </div>
          <div className="vocabulary_next_item tertiary">
            {(trainingPlanDuration === 4 || trainingPlanDuration === 5) &&
            boostStatus?.has_active_boost ? (
              <FiZap
                size={24}
                className="vocabulary_next_icon tertiary"
                style={{ color: 'var(--liwaza-primary)' }}
              />
            ) : (
              <FiZap size={24} className="vocabulary_next_icon tertiary" />
            )}
            <p className="vocabulary_next_text tertiary">
              {(trainingPlanDuration === 4 || trainingPlanDuration === 5) &&
              boostStatus?.has_active_boost ? (
                <>
                  <strong style={{ color: 'var(--liwaza-primary)' }}>
                    {adjustedPlanDetails.wordsPerDay} words daily (BOOSTED)
                  </strong>
                  <br />
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    Boost ends in {boostStatus.days_remaining} days
                    <br />
                    Then{' '}
                    {
                      calculateArticlesPerDay(trainingPlanDuration).wordsPerDay
                    }{' '}
                    words/day normal
                  </span>
                </>
              ) : (
                <>
                  {hasTakenMockTest &&
                  trainingPlanDuration &&
                  adjustedPlanDetails
                    ? adjustedPlanDetails.wordsPerDay
                    : DAYS_OPTIONS.find((opt) => opt.days === daysSelected)
                        ?.wordsPerDay}{' '}
                  new words daily
                </>
              )}
            </p>
          </div>
          <div className="vocabulary_next_item surface">
            <FiClock size={24} className="vocabulary_next_icon surface" />
            <p className="vocabulary_next_text surface vocabulary_next_time">
              Delivered at{' '}
              {new Date(`2000-01-01T${preferredTime}`).toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })}
            </p>
            <p className="vocabulary_next_timezone">{displayTimezone}</p>
          </div>
        </div>
      </div>

      {/* Initial Articles Section */}
      {articles.length > 0 && (
        <div className="vocabulary_articles_section">
          <h3 className="vocabulary_articles_header">
            <FiGift /> Your First Articles Are Ready!
          </h3>
          <p className="vocabulary_articles_intro">
            Start your vocabulary journey right now with these carefully
            selected articles:
          </p>

          <div className="vocabulary_articles_grid">
            {articles.map((article, index) => {
              const progress = getNormalizedArticleProgress(
                article.id,
                userEmail,
                article
              );
              const totalExercises = progress.totalExercises;
              const status = getArticleStatus(
                article.id,
                userEmail,
                totalExercises,
                progress.totalWords
              );

              const totalWords = progress.totalWords;
              return (
                <div
                  key={article.id}
                  className={`vocabulary_article_card ${status}`}
                >
                  <div className="vocabulary_article_header">
                    <div className="vocabulary_article_number">{index + 1}</div>
                    <h4 className="vocabulary_article_title">
                      {article.title}
                    </h4>
                    <span className={`vocabulary_status_badge ${status}`}>
                      {status === 'done' && <FiCheckCircle size={16} />}
                      {status === 'completed' && <FiRefreshCw size={16} />}
                      {status === 'read' && <FiBookOpen size={16} />}
                      {status === 'opened' && <FiEye size={16} />}
                      {status === 'not_started' && <FiPlay size={16} />}
                      {status === 'done'
                        ? 'Done'
                        : status === 'completed'
                          ? 'Completed'
                        : status === 'read'
                          ? 'In Progress'
                          : status === 'opened'
                            ? 'Opened'
                            : 'Not Started'}
                    </span>
                  </div>

                  <p className="vocabulary_article_excerpt">
                    {article.excerpt}
                  </p>

                  {/* Progress Information */}
                  {status !== 'not_started' && (
                    <div className="vocabulary_progress_info">
                      <div className="vocabulary_progress_item">
                        <FiBookOpen size={14} />
                        <span>
                          Article {status === 'opened' ? 'opened' : 'read'}
                        </span>
                      </div>
                      {totalExercises > 0 && (
                        <div className="vocabulary_progress_item">
                          <FiCheck size={14} />
                          <span>
                            {progress.exercisesCompleted}/{totalExercises}{' '}
                            exercises
                          </span>
                        </div>
                      )}
                      {totalWords > 0 && (
                        <div className="vocabulary_progress_item">
                          <FiStar size={14} />
                          <span>
                            {progress.wordsMastered || 0}/{totalWords} words
                            mastered
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="vocabulary_article_meta">
                    <div className="vocabulary_article_badges">
                      <span className="vocabulary_article_badge">
                        <FiClock /> {article.estimated_reading_time} min
                      </span>
                      <span className="vocabulary_article_badge">
                        <FiBookOpen /> {totalWords} words
                      </span>
                      <span className="vocabulary_article_badge">
                        <FiCheck /> {totalExercises} exercises
                      </span>
                      <span
                        className={`vocabulary_difficulty_badge ${article.difficulty_level}`}
                      >
                        {article.difficulty_level}
                      </span>
                    </div>

                    <button
                      onClick={() => {
                        const articleRef = getGreArticlePublicRef(article);
                        if (!articleRef) {
                          return;
                        }

                        navigate(
                          `/exams/gre/verbal/vocabulary/dashboard/?article=${articleRef}`
                        );
                      }}
                      className={`vocabulary_start_btn ${status}`}
                    >
                    {status === 'done' ? (
                        <>
                          <FiCheckCircle /> Review Article
                        </>
                    ) : status === 'completed' ? (
                      <>
                        <FiRefreshCw /> Review Mistakes
                      </>
                      ) : status === 'read' ? (
                        <>
                          <FiBookOpen /> Continue Reading
                        </>
                      ) : status === 'opened' ? (
                        <>
                          <FiArrowRight /> Continue Reading
                        </>
                      ) : (
                        <>
                          <FiPlay /> Start Reading
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="vocabulary_confirmation_center">
        <button
          onClick={() => setStep('progress')}
          className="vocabulary_view_progress_btn"
        >
          <FiTrendingUp /> View Progress Dashboard
        </button>
      </div>
    </div>
  );

  const renderProgress = () => (
    <div className="vocabulary_progress">
      {/* Liwaza GRE Book - Always visible first */}
      {renderLiwazaGREBook()}
      <div className="vocabulary_progress_header">
        <h2 className="vocabulary_progress_title">Vocabulary Progress</h2>

        {/* Show plan information and time adjustment button */}
        <div className="vocabulary_plan_controls">
          {/* Plan Information */}
          {(trainingPlanDuration && adjustedPlanDetails) ||
          (step === 'progress' && !trainingPlanDuration) ? (
            <div className="vocabulary_adjusted_plan_info">
              <FiTarget size={20} />
              <div className="vocabulary_plan_details">
                <span className="vocabulary_plan_duration">
                  {trainingPlanDuration
                    ? `${userSelectedDuration || trainingPlanDuration}-Month ${(userSelectedDuration || trainingPlanDuration) > trainingPlanDuration ? 'Extended' : fromTrainingPlan ? 'Adjusted' : 'Personalized'} Plan`
                    : `${userSelectedDuration || 4}-Month ${userSelectedDuration && userSelectedDuration > 4 ? 'Extended' : 'Default'} Plan`}
                </span>
                <span className="vocabulary_plan_articles">
                  {trainingPlanDuration && adjustedPlanDetails
                    ? `${adjustedPlanDetails.articlesPerDay} articles/day • ${adjustedPlanDetails.wordsPerDay} words/day`
                    : (trainingPlanDuration === 4 ||
                          trainingPlanDuration === 5) &&
                        boostStatus?.has_active_boost
                      ? `${Math.min(2 * boostStatus.boost_multiplier, 5)} articles/day • ${Math.min(2 * boostStatus.boost_multiplier, 5) * 5} words/day`
                      : '2 articles/day • 10 words/day'}
                </span>
                <span className="vocabulary_plan_time">
                  <FiClock size={14} />
                  Delivered at{' '}
                  {new Date(`2000-01-01T${preferredTime}`).toLocaleTimeString(
                    [],
                    {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    }
                  )}
                </span>
                {(trainingPlanDuration === 4 || trainingPlanDuration === 5) &&
                  boostStatus?.has_active_boost && (
                    <span className="vocabulary_boost_end_time">
                      <FiZap size={14} />
                      Boost ends in {boostStatus.days_remaining} day
                      {boostStatus.days_remaining !== 1 ? 's' : ''}
                    </span>
                  )}
              </div>

              <div className="vocabulary_plan_actions">
                {/* Boost Status Indicator (above Adjust Time button) - Only for 4 and 5 month plans */}
                {(trainingPlanDuration === 4 || trainingPlanDuration === 5) &&
                  boostStatus?.has_active_boost && (
                    <div className="vocabulary_boost_indicator">
                      <FiZap size={12} /> {boostStatus.boost_multiplier}x
                      Boosted
                      <InfoTooltip
                        content={`Article Boost is active! You're receiving ${boostStatus.boost_multiplier}x more articles per day for ${boostStatus.days_remaining} days. The boost increases your daily articles up to a maximum of 5 articles (25 words) per day. Use the "Remove Boost" button to deactivate early if needed.`}
                        position="bottom"
                        maxWidth="320px"
                      />
                    </div>
                  )}

                {/* Plan Adjustment Button */}
                <button
                  onClick={() => setStep('setup')}
                  className="vocabulary_time_adjust_btn"
                  title="Change your plan duration or preferred article delivery time"
                >
                  <FiClock size={16} />
                  Adjust Plan
                </button>

                {/* Boost Articles Button - Only for 4 and 5 month plans */}
                {(trainingPlanDuration === 4 || trainingPlanDuration === 5) &&
                boostStatus?.has_active_boost ? (
                  <button
                    onClick={handleDeactivateBoost}
                    className="vocabulary_boost_btn vocabulary_boost_deactivate_btn"
                    title={`Deactivate ${boostStatus.boost_multiplier}x article boost (${boostStatus.days_remaining} days remaining)`}
                    disabled={boostLoading}
                  >
                    <FiTrash size={16} />
                    {boostLoading
                      ? 'Deactivating...'
                      : `Remove ${boostStatus.boost_multiplier}x Boost`}
                  </button>
                ) : (
                  (trainingPlanDuration === 4 ||
                    trainingPlanDuration === 5) && (
                    <button
                      onClick={handleEnableBoost}
                      className="vocabulary_boost_btn vocabulary_boost_enable_btn"
                      title="Enable 2x article boost for 7 days"
                      disabled={boostLoading}
                    >
                      <FiZap size={16} />
                      {boostLoading ? 'Enabling...' : 'Boost Articles'}
                      <InfoTooltip
                        content="Boost your vocabulary learning! Get 2x more articles per day for 7 days. This temporarily increases your daily articles up to a maximum of 5 articles (25 words) to accelerate your learning. Perfect for intensive study periods or when you want to catch up quickly."
                        position="bottom"
                        maxWidth="320px"
                      />
                    </button>
                  )
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Progress Stats */}
      <div className="vocabulary_progress_stats">
        <div className="vocabulary_stat_card">
          <FiTrendingUp className="vocabulary_stat_icon primary" />
          <h3 className="vocabulary_stat_title">
            Words Mastered
            <InfoTooltip
              content="Total number of GRE vocabulary words you have mastered through daily articles and practice. Mastering more words increases your reading comprehension and verbal reasoning scores. Aim for 800+ words for elite GRE performance."
              position="top"
              maxWidth="300px"
            />
          </h3>
          <p className="vocabulary_stat_value primary">
            {progress?.masteredWords || 0} / {progress?.totalWords || 0}
          </p>
          {showVerbal155TargetHint && (
            <div className="vocabulary_stat_alert">
              <FiTarget className="vocabulary_stat_alert_icon" />
              <span>
                Aim for <strong>700+ mastered words</strong> to target{' '}
                <strong>155+</strong> in GRE Verbal
              </span>
            </div>
          )}
        </div>

        <div className="vocabulary_stat_card">
          <FiRepeat className="vocabulary_stat_icon secondary" />
          <h3 className="vocabulary_stat_title">
            Current Streak
            <InfoTooltip
              content="Number of consecutive days you've practiced vocabulary. Keeping a daily streak helps you retain new words and build a strong study habit. Aim for a 7+ day streak for best results."
              position="top"
              maxWidth="300px"
            />
          </h3>
          <p className="vocabulary_stat_value secondary">
            {progress?.streak || 0} days
          </p>
        </div>

        <div className="vocabulary_stat_card">
          <FiBook className="vocabulary_stat_icon tertiary" />
          <h3 className="vocabulary_stat_title">
            Accuracy
            <InfoTooltip
              content="Percentage of vocabulary questions you answered correctly. High accuracy means you are truly mastering the words, not just memorizing. Aim for 85%+ accuracy to be ready for the GRE."
              position="top"
              maxWidth="300px"
            />
          </h3>
          <p className="vocabulary_stat_value tertiary">
            {Math.round(progress?.accuracy || 0)}%
          </p>
        </div>

        <div className="vocabulary_stat_card">
          <FiClock className="vocabulary_stat_icon surface" />
          <h3 className="vocabulary_stat_title">
            Reading Speed
            <InfoTooltip
              content="Your average reading speed in words per minute (WPM). A good reading speed for GRE articles is 250-350 WPM. Higher reading speed helps you complete reading comprehension sections faster."
              position="top"
              maxWidth="300px"
            />
          </h3>
          <p className="vocabulary_stat_value surface">
            {progress?.averageWPM || 0} WPM
          </p>
        </div>
      </div>

      {/* Weekly Learning Progression */}
      <div className="vocabulary_weekly_progression">
        <h3 className="vocabulary_weekly_header">
          <FiCalendar /> Learning Progression
        </h3>

        {/* Daily Progress Grid */}
        {progress?.weeklyProgress &&
          Object.keys(progress.weeklyProgress).length > 0 && (
            <div className="vocabulary_daily_grid">
              <h4 className="vocabulary_daily_title">
                <FiActivity /> Daily Vocabulary Progress
              </h4>
              <div className="vocabulary_daily_cards">
                {Object.entries(progress.weeklyProgress)
                  .slice(-7) // Show last 7 days
                  .map(([date, wordsLearned]) => {
                    const dayDate = new Date(date);
                    const isToday =
                      dayDate.toDateString() === new Date().toDateString();
                    const dayName = dayDate.toLocaleDateString([], {
                      weekday: 'short',
                    });
                    const dayNumber = dayDate.getDate();

                    return (
                      <div
                        key={date}
                        className={`vocabulary_daily_card ${wordsLearned > 0 ? 'active' : 'inactive'} ${isToday ? 'today' : ''}`}
                      >
                        <div className="vocabulary_daily_date">
                          <div className="vocabulary_day_name">{dayName}</div>
                          <div className="vocabulary_day_number">
                            {dayNumber}
                          </div>
                        </div>

                        {wordsLearned > 0 ? (
                          <div className="vocabulary_daily_stats">
                            <div className="vocabulary_words_count">
                              {wordsLearned}
                            </div>
                            <div className="vocabulary_words_label">words</div>
                          </div>
                        ) : (
                          <div className="vocabulary_no_activity">
                            <div className="vocabulary_no_words">0</div>
                            <div className="vocabulary_no_label">words</div>
                          </div>
                        )}

                        {isToday && (
                          <div className="vocabulary_today_indicator">
                            Today
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

        {/* Monthly Progress Summary */}
        {progress?.monthlyProgress &&
          Object.keys(progress.monthlyProgress).length > 0 && (
            <div className="vocabulary_monthly_summary">
              <h4 className="vocabulary_monthly_title">
                <FiTrendingUp /> Weekly Totals
              </h4>
              <div className="vocabulary_monthly_cards">
                {Object.entries(progress.monthlyProgress).map(
                  ([week, totalWords]) => (
                    <div key={week} className="vocabulary_monthly_card">
                      <div className="vocabulary_monthly_week">{week}</div>
                      <div className="vocabulary_monthly_count">
                        {totalWords}
                      </div>
                      <div className="vocabulary_monthly_label">
                        words mastered
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

        {/* Reading Speed Progress */}
        {progress?.averageWPM > 0 && (
          <div className="vocabulary_reading_progress">
            <h4 className="vocabulary_reading_title">
              <FiClock /> Reading Speed Progress
            </h4>
            <div className="vocabulary_reading_stats">
              <div className="vocabulary_reading_card primary">
                <div className="vocabulary_reading_value">
                  {progress.averageWPM}
                </div>
                <div className="vocabulary_reading_label">Average WPM</div>
                <div className="vocabulary_reading_desc">
                  {progress.averageWPM >= 300
                    ? 'Excellent!'
                    : progress.averageWPM >= 250
                      ? 'Good pace'
                      : 'Keep practicing'}
                </div>
              </div>

              <div className="vocabulary_reading_card secondary">
                <div className="vocabulary_reading_value">
                  {progress.articlesRead || 0}
                </div>
                <div className="vocabulary_reading_label">Articles Read</div>
                <div className="vocabulary_reading_desc">
                  {Math.round((progress.totalReadingTime || 0) / 60)} min total
                </div>
              </div>

              <div className="vocabulary_reading_card tertiary">
                <div className="vocabulary_reading_value">
                  {progress.averageWPM >= 350
                    ? '🚀'
                    : progress.averageWPM >= 300
                      ? '⚡'
                      : progress.averageWPM >= 250
                        ? '📈'
                        : '📚'}
                </div>
                <div className="vocabulary_reading_label">Speed Level</div>
                <div className="vocabulary_reading_desc">Target: 300+ WPM</div>
              </div>
            </div>
          </div>
        )}

        {/* Learning Streak Visualization */}
        {progress?.streak > 0 && (
          <div className="vocabulary_streak_section">
            <h4 className="vocabulary_streak_title">
              <FiZap /> Learning Streak
            </h4>
            <div className="vocabulary_streak_display">
              <div className="vocabulary_streak_main">
                <div className="vocabulary_streak_number">
                  {progress.streak}
                </div>
                <div className="vocabulary_streak_text">days in a row</div>
              </div>

              <div className="vocabulary_streak_motivator">
                {progress.streak >= 30
                  ? '🏆 Vocabulary Master!'
                  : progress.streak >= 14
                    ? "🔥 You're on fire!"
                    : progress.streak >= 7
                      ? '⚡ Great momentum!'
                      : progress.streak >= 3
                        ? '📈 Building habits!'
                        : '🌱 Keep it up!'}
              </div>

              <div className="vocabulary_streak_next">
                {progress.streak < 30 && (
                  <>
                    <div className="vocabulary_streak_bar">
                      <div
                        className="vocabulary_streak_fill"
                        style={{
                          width: `${Math.min((progress.streak / 30) * 100, 100)}%`,
                        }}
                      ></div>
                    </div>
                    <div className="vocabulary_streak_goal">
                      {30 - progress.streak} days to Vocabulary Master
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Revise past articles */}
      <div className="vocabulary_game_section">
        <h3 className="vocabulary_game_header">
          <FiRefreshCw /> Revise past articles
        </h3>
        <div className="vocabulary_game_card">
          <div className="vocabulary_game_content">
            <div className="vocabulary_game_icon">
              <FiBookOpen size={32} />
            </div>
            <div className="vocabulary_game_info">
              <h4 className="vocabulary_game_title">Article history</h4>
              <p className="vocabulary_game_description">
                Re-read texts you have already completed and redo their
                exercises to reinforce vocabulary and comprehension.
              </p>
              <div className="vocabulary_game_stats">
                <div className="vocabulary_game_stat">
                  <FiCheckCircle size={16} />
                  <span>
                    {progress?.completedArticlesCount ||
                      progress?.articlesRead ||
                      0}{' '}
                    articles completed
                  </span>
                </div>
                {progress?.recentArticles?.[0]?.read_date && (
                  <div className="vocabulary_game_stat">
                    <FiCalendar size={16} />
                    <span>Last: {progress.recentArticles[0].read_date}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <button
            className="vocabulary_game_configure_btn"
            onClick={() =>
              navigate('/exams/gre/verbal/vocabulary/history')
            }
          >
            <FiArrowRight size={16} />
            {(progress?.completedArticlesCount || progress?.articlesRead || 0) >
            0
              ? 'See all & revise'
              : 'Open article history'}
          </button>
        </div>
      </div>

      {/* Daily Articles Section */}
      <div className="vocabulary_daily_articles">
        <h3 className="vocabulary_daily_header">
          <FiCalendar /> Today's Articles
        </h3>
        {articles.length > 0 ? (
          <div className="vocabulary_articles_list">
            {articles.map((article, index) => {
              const progress = getNormalizedArticleProgress(
                article.id,
                userEmail,
                article
              );
              const totalExercises = progress.totalExercises;
              const status = getArticleStatus(
                article.id,
                userEmail,
                totalExercises,
                progress.totalWords
              );
              const totalWords = progress.totalWords;

              return (
                <div
                  key={article.id}
                  className={`vocabulary_article_preview ${status}`}
                >
                  {/* Numbered Circle */}
                  <div className="vocabulary_preview_number">{index + 1}</div>

                  {/* Content Section */}
                  <div className="vocabulary_preview_content">
                    <h4 className="vocabulary_preview_title">
                      {article.title}
                      <span className={`vocabulary_status_badge ${status}`}>
                        {status === 'done' && <FiCheckCircle size={16} />}
                        {status === 'completed' && <FiRefreshCw size={16} />}
                        {status === 'read' && <FiBookOpen size={16} />}
                        {status === 'opened' && <FiEye size={16} />}
                        {status === 'not_started' && <FiPlay size={16} />}
                        {status === 'done'
                          ? 'Done'
                          : status === 'completed'
                            ? 'Completed'
                          : status === 'read'
                            ? 'In Progress'
                            : status === 'opened'
                              ? 'Opened'
                              : 'Not Started'}
                      </span>
                    </h4>
                    <p className="vocabulary_preview_excerpt">
                      {article.excerpt}
                    </p>

                    {/* Progress Information */}
                    {status !== 'not_started' && (
                      <div className="vocabulary_progress_info">
                        <div className="vocabulary_progress_item">
                          <FiBookOpen size={14} />
                          <span>
                            Article {status === 'opened' ? 'opened' : 'read'}
                          </span>
                        </div>
                        {totalExercises > 0 && (
                          <div className="vocabulary_progress_item">
                            <FiCheck size={14} />
                            <span>
                              {progress.exercisesCompleted}/{totalExercises}{' '}
                              exercises
                            </span>
                          </div>
                        )}
                        {totalWords > 0 && (
                          <div className="vocabulary_progress_item">
                            <FiStar size={14} />
                            <span>
                              {progress.wordsMastered || 0}/{totalWords} words
                              mastered
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Badges Row */}
                    <div className="vocabulary_preview_badges">
                      <span className="vocabulary_preview_badge">
                        <FiClock size={14} /> {article.estimated_reading_time}{' '}
                        min
                      </span>
                      <span className="vocabulary_preview_badge">
                        <FiBookOpen size={14} /> {totalWords} words
                      </span>
                      <span className="vocabulary_preview_badge">
                        <FiCheck size={14} /> {totalExercises} exercises
                      </span>
                      <span
                        className={`vocabulary_preview_difficulty ${article.difficulty_level}`}
                      >
                        {article.difficulty_level}
                      </span>
                    </div>
                  </div>

                  {/* Start Reading Button */}
                  <button
                    onClick={() => {
                      const articleRef = getGreArticlePublicRef(article);
                      if (!articleRef) {
                        return;
                      }

                      navigate(
                        `/exams/gre/verbal/vocabulary/dashboard/?article=${articleRef}`
                      );
                    }}
                    className={`vocabulary_preview_btn ${status}`}
                  >
                    {status === 'done' ? (
                      <>
                        <FiCheckCircle size={16} /> Review Article
                      </>
                    ) : status === 'completed' ? (
                      <>
                        <FiRefreshCw size={16} /> Review Mistakes
                      </>
                    ) : status === 'read' ? (
                      <>
                        <FiBookOpen size={16} /> Continue Reading
                      </>
                    ) : status === 'opened' ? (
                      <>
                        <FiArrowRight size={16} /> Continue Reading
                      </>
                    ) : (
                      <>
                        <FiPlay size={16} /> Start Reading
                      </>
                    )}
                  </button>
                </div>
              );
            })}
            <GetStartedWith
              title="Didn't understand something?"
              spaceName="Verbal vault"
              humanCoachTitle="Talk to a GRE coach"
              liwazaAITitle="Ask AI"
              aifirstmessage="I need help understanding the articles"
              aicontext={`understanding the daily articles: ${articles.map((article) => article.title).join(', ')}`}
              communityTitle="Ask other students"
              aiOnly={true}
            />
          </div>
        ) : (
          <div className="vocabulary_no_articles">
            <FiBookOpen size={48} className="vocabulary_no_articles_icon" />
            <p>No articles available today. Check back tomorrow!</p>
          </div>
        )}
      </div>

      {/* Vocabulary Review Game Section */}
      <VocabularyGameSection
        progress={progress}
        userEmail={userEmail}
        navigate={navigate}
      />

      {/* Flashcard Book Section */}
      <div className="vocabulary_game_section">
        <h3 className="vocabulary_game_header">
          <FiBookmark /> Flashcard Book
        </h3>

        <div className="vocabulary_game_card">
          <div className="vocabulary_game_content">
            <div className="vocabulary_game_icon">
              <FiBookmark size={32} />
            </div>

            <div className="vocabulary_game_info">
              <h4 className="vocabulary_game_title">Study with Flashcards</h4>
              <p className="vocabulary_game_description">
                Access your personalized flashcard collection with spaced
                repetition. Add emoticons, personal notes, and review words at
                optimal intervals for maximum retention.
              </p>

              <div className="vocabulary_game_stats">
                <div className="vocabulary_game_stat">
                  <FiBook size={16} />
                  <span>Spaced repetition</span>
                </div>
                <div className="vocabulary_game_stat">
                  <FiStar size={16} />
                  <span>Add emoticons & personal notes</span>
                </div>
                <div className="vocabulary_game_stat">
                  <FiTarget size={16} />
                  <span>Filter by difficulty & status</span>
                </div>
              </div>
            </div>
          </div>

          <button
            className="vocabulary_game_configure_btn"
            onClick={() => navigate('/exams/gre/verbal/vocabulary/flashcards')}
          >
            <FiBookmark size={16} />
            Open Flashcard Book
          </button>
        </div>
      </div>
    </div>
  );

  if (loading && step === 'intro') {
    return (
      <div className="layout-loading">
        <div className="loading-spinner"></div>
        <p>Loading vocabulary mastery and checking your enrollment...</p>
      </div>
    );
  }

  return (
    <div className="vocabulary_mastery">
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          {toast.message}
        </div>
      )}
      {step === 'intro' && renderIntro()}
      {step === 'setup' && renderSetup()}
      {step === 'confirmation' && renderConfirmation()}
      {step === 'progress' && renderProgress()}
    </div>
  );
}
